import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BattleUI from '../BattleUI';
import SimpleTutorialDialog from './SimpleTutorialDialog';
import { useCampaignState } from './useCampaignState';
import { getWebSocketAuthMessage, API_URL, WS_URL } from '../config/apiConfig';
import { updateDiscordPresence, clearDiscordPresence } from '../hooks/useDiscordPresence';
import { needsTokenRefresh, refreshToken, isTokenValid } from '../auth';
import { useNotifications } from '../notifications';
import { opportunist302AllowsPassTurnDuringAttackGeneralStep } from './opportunist302AttackGeneralTutorialGate';

async function reportCampaignStartFailure(reason, missionId, userId, details) {
  try {
    await fetch(`${API_URL}/campaign/log-start-failure`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reason, missionId, userId, details })
    });
  } catch (e) {
    console.error('❌ Campaign: Failed to report start failure to server:', e);
  }
}

/**
 * Campaign Battle UI Wrapper
 * Wraps BattleUI with campaign-specific features:
 * - Tutorial system
 * - Campaign mode detection
 * - Script execution integration
 */
function CampaignBattleUI({ user, userId, token, onLogout }) {
  const { missionId } = useParams();
  const navigate = useNavigate();
  const { showNotification, NotificationComponent } = useNotifications();
  const [gameId, setGameId] = useState(null);
  const [gameState, setGameState] = useState(null);
  const [missionScript, setMissionScript] = useState(null);
  const lastCampaignStateRef = useRef(null);
  const { currentTutorial, isPaused, continueTutorial, advanceToTutorial, addTutorial, clearTutorials } = useCampaignState(gameState);

  // Suppress tutorial dialog while waiting for the player to use Missile Barrage (mission 3)
  const [suppressTutorialDialog, setSuppressTutorialDialog] = useState(false);

  const handleGameStateChange = useCallback((newState) => {
    if (!newState) { setGameState(newState); return; }
    if (gameId?.startsWith('campaign_') && !newState.campaignMode && lastCampaignStateRef.current) {
      const prev = lastCampaignStateRef.current.scriptState;
      setGameState({ ...newState, campaignMode: true, missionId: newState.missionId || lastCampaignStateRef.current.missionId, scriptState: (newState.scriptState?.tutorialQueue?.length ? newState.scriptState : { ...(newState.scriptState || {}), tutorialQueue: prev?.tutorialQueue || [] }) });
      return;
    }
    if (newState.campaignMode && newState.scriptState) lastCampaignStateRef.current = { campaignMode: true, missionId: newState.missionId, scriptState: newState.scriptState };
    setGameState(newState);
  }, [gameId]);

  // Discord Rich Presence: Update when campaign battle starts
  useEffect(() => {
    if (gameState && gameId) {
      // Campaign battle has started - update Discord presence
      updateDiscordPresence('In Battle', 'Campaign Mode', true);
    }
    
    // Cleanup: Clear presence when component unmounts (battle ends)
    return () => {
      clearDiscordPresence();
    };
  }, [gameState, gameId]);
  
  // Compute blocked order IDs for tutorial (e.g. Mission 3: block Scavenger/Kek/Missile Barrage until required)
  // Supports turn-based (playerTurnIndex) and tutorial-based blocking
  // While "Missile Barrage" dialog is visible, treat as "Deploy Effects" so all orders stay blocked.
  // When suppressTutorialDialog is true (after Continue), use "Missile Barrage" rules (only 316 unblocked).
  const blockedOrderIds = useMemo(() => {
    if (missionId !== 'chapter_1_mission_3') return [];
    const blocked = [];
    const t = suppressTutorialDialog
      ? 'Missile Barrage'
      : (currentTutorial?.stepId === 'missile_barrage' ? 'Deploy Effects' : (currentTutorial?.stepId || currentTutorial?.title));
    // 286 Scavenger Operative: always blocked (not in tutorial)
    blocked.push(286);
    // 239 Kek: unblocked from "Enemy Drones" onward (stepId or title) so card looks active
    const kekStepsAllowed = [
      'Enemy Drones', 'Destroy the Drone', 'Deploy Effects', 'Missile Barrage', 'Your Drones', 'Use Your Drone', 'Samuel Johnson', 'Counter Drones', 'Protect Your Units', 'Finish the Fight',
      'enemy_drones', 'destroy_the_drone', 'deploy_effects', 'missile_barrage', 'your_drones', 'use_your_drone', 'samuel_johnson', 'counter_drones', 'protect_your_units', 'finish_the_fight'
    ];
    if (!t || !kekStepsAllowed.includes(t)) {
      blocked.push(239);
    }
    // 316 Missile Barrage: blocked until after "Missile Barrage" Continue is clicked (suppressTutorialDialog)
    if (!t || !['Missile Barrage', 'Your Drones', 'Use Your Drone', 'Samuel Johnson', 'Counter Drones', 'Protect Your Units', 'Finish the Fight'].includes(t)) {
      blocked.push(316);
    }
    // 398 Aerial Strike Drone: unblocked when "Your Drones" step is visible (stepId or title)
    const droneStepsAllowed = [
      'Your Drones', 'Use Your Drone', 'Samuel Johnson', 'Counter Drones', 'Protect Your Units', 'Finish the Fight',
      'your_drones', 'use_your_drone', 'samuel_johnson', 'counter_drones', 'protect_your_units', 'finish_the_fight'
    ];
    if (!t || !droneStepsAllowed.includes(t)) {
      blocked.push(398);
    }
    return blocked;
  }, [missionId, currentTutorial?.stepId, currentTutorial?.title, suppressTutorialDialog]);

  // Mission 4: compute allowed order IDs per tutorial step (null = free play / all allowed)
  const tutorial4AllowedOrders = useMemo(() => {
    if (missionId !== 'chapter_1_mission_4') return null;
    const t = currentTutorial?.stepId || currentTutorial?.title;
    const map = {
      'Mulligan': [], "Ma'at Arrives": [], "Ma'at's Strength": [],
      'Devastating Impact': [], 'Advanced Tactics': [282, 314], 'Hold the Line': [288],
      'Mentora': [], 'Shield Mastery': [309, 287, 317], 'Patience': [],
      'Clearing the Field': [291, 325], 'Well Done': [], 'Reinforcements': [],
      "Harral's Betrayal": [307], "End the threat": null,
    };
    if (!t) return null;
    return t in map ? map[t] : null;
  }, [missionId, currentTutorial?.title]);

  // Optional tutorial overlay config: step config for current message (from missionId_tutorial_ui.json)
  const overlayStepConfig = useMemo(() => {
    const overlay = gameState?.scriptState?.tutorialOverlay;
    if (!overlay || !overlay.messages || !(currentTutorial?.stepId || currentTutorial?.title)) return null;
    const key = currentTutorial.stepId || currentTutorial.title;
    return overlay.messages[key] || null;
  }, [gameState?.scriptState?.tutorialOverlay, currentTutorial?.stepId, currentTutorial?.title]);

  // When overlay defines clickableInHand, only those order IDs are allowed (same semantics as tutorial4AllowedOrders)
  const overlayAllowedOrders = useMemo(() => {
    if (!overlayStepConfig) return null;
    const arr = overlayStepConfig.clickableInHand;
    return Array.isArray(arr) ? arr : null;
  }, [overlayStepConfig]);

  // Store BattleUI element in ref - only recreate when gameId changes.
  // Order blocking is handled via DOM manipulation (same pattern as zone blocking).
  const battleUIElementRef = useRef(null);
  const lastGameIdRef = useRef(null);

  if (gameState && gameId && gameId !== lastGameIdRef.current) {
    lastGameIdRef.current = gameId;
    battleUIElementRef.current = (
      <BattleUI
        user={user}
        userId={userId || parseInt(localStorage.getItem('userId'))}
        token={token}
        onLogout={onLogout}
        initialGameState={gameState}
        initialGameId={gameId}
        onGameStateChange={handleGameStateChange}
      />
    );
  }
  
  // DOM-based order card blocking — applies pointer-events/opacity directly to carousel cards
  // AND their portal-rendered button containers (Deploy/Request buttons rendered on document.body).
  // Works independently of React re-renders (same pattern as zone blocking).
  // Overlay config (tutorial_ui.json): when overlayAllowedOrders is set, only those IDs are clickable.
  useEffect(() => {
    const useOverlay = overlayAllowedOrders !== null;
    if (missionId !== 'chapter_1_mission_3' && missionId !== 'chapter_1_mission_4' && !useOverlay) return;
    const deactivatedSet = overlayStepConfig?.deactivatedInHand && Array.isArray(overlayStepConfig.deactivatedInHand)
      ? new Set(overlayStepConfig.deactivatedInHand) : null;
    const applyBlocking = () => {
      const cards = document.querySelectorAll('[data-order-id]');
      cards.forEach(card => {
        const orderId = parseInt(card.getAttribute('data-order-id'), 10);
        let isBlocked = false;
        if (useOverlay) {
          isBlocked = !overlayAllowedOrders.includes(orderId);
        } else if (missionId === 'chapter_1_mission_3') {
          isBlocked = blockedOrderIds.includes(orderId);
        } else if (missionId === 'chapter_1_mission_4') {
          isBlocked = tutorial4AllowedOrders !== null && !tutorial4AllowedOrders.includes(orderId);
        }
        const isDeactivatedOnly = deactivatedSet && deactivatedSet.has(orderId) && !isBlocked;
        if (isBlocked) {
          card.style.pointerEvents = 'none';
          card.style.opacity = '0.6';
          card.style.cursor = 'not-allowed';
        } else if (isDeactivatedOnly) {
          card.style.pointerEvents = '';
          card.style.opacity = '0.6';
          card.style.cursor = '';
        } else {
          card.style.pointerEvents = '';
          card.style.opacity = '';
          card.style.cursor = '';
        }
      });
      const btnContainers = document.querySelectorAll('[data-order-buttons-id]');
      btnContainers.forEach(container => {
        const orderId = parseInt(container.getAttribute('data-order-buttons-id'), 10);
        let isBlocked = false;
        if (useOverlay) {
          isBlocked = !overlayAllowedOrders.includes(orderId);
        } else if (missionId === 'chapter_1_mission_3') {
          isBlocked = blockedOrderIds.includes(orderId);
        } else if (missionId === 'chapter_1_mission_4') {
          isBlocked = tutorial4AllowedOrders !== null && !tutorial4AllowedOrders.includes(orderId);
        }
        if (isBlocked) {
          container.style.pointerEvents = 'none';
          container.style.opacity = '0.4';
          container.style.display = 'none';
        } else {
          container.style.pointerEvents = '';
          container.style.opacity = '';
          container.style.display = '';
        }
      });
    };
    applyBlocking();
    const iv = setInterval(applyBlocking, 200);
    return () => {
      clearInterval(iv);
      const cards = document.querySelectorAll('[data-order-id]');
      cards.forEach(card => {
        card.style.pointerEvents = '';
        card.style.opacity = '';
        card.style.cursor = '';
      });
      const btnContainers = document.querySelectorAll('[data-order-buttons-id]');
      btnContainers.forEach(container => {
        container.style.pointerEvents = '';
        container.style.opacity = '';
        container.style.display = '';
      });
    };
  }, [missionId, blockedOrderIds, tutorial4AllowedOrders, overlayAllowedOrders, overlayStepConfig]);

  // Use direct DOM manipulation for highlighting - doesn't cause React re-renders
  useEffect(() => {
    const highlight = currentTutorial?.highlight || null;
    const zoneElements = [
      document.querySelector('.battle-zone.opponent-front'),
      document.querySelector('.battle-zone.opponent-flank'),
      document.querySelector('.battle-zone.front'),
      document.querySelector('.battle-zone.flank')
    ].filter(Boolean);
    
    // Get or create highlight overlay element
    let highlightOverlay = document.getElementById('tutorial-highlight-overlay');
    if (!highlightOverlay) {
      highlightOverlay = document.createElement('div');
      highlightOverlay.id = 'tutorial-highlight-overlay';
      highlightOverlay.style.position = 'fixed';
      highlightOverlay.style.zIndex = '99998';
      highlightOverlay.style.pointerEvents = 'none';
      document.body.appendChild(highlightOverlay);
    }
    
    // ALWAYS clear all previous highlights first - ensures clean state for every tutorial message
    zoneElements.forEach(el => {
      if (el) {
        el.style.outline = '';
        el.style.boxShadow = '';
        el.classList.remove('tutorial-zone-pulsate');
      }
    });
    
    // Clear order image pulsation
    const orderCarousel = document.querySelector('.order-carousel');
    if (orderCarousel) {
      const allOrderCards = orderCarousel.querySelectorAll('.carousel-order-card');
      allOrderCards.forEach(card => {
        card.classList.remove('tutorial-order-image-pulsate');
      });
    }
    
    // Clear order pulsation interval if it exists
    if (typeof window.tutorialOrderPulsationInterval !== 'undefined') {
      clearInterval(window.tutorialOrderPulsationInterval);
      delete window.tutorialOrderPulsationInterval;
    }
    // Always reset the highlight overlay completely
    if (highlightOverlay) {
      highlightOverlay.style.display = 'none';
      highlightOverlay.style.outline = '';
      highlightOverlay.style.boxShadow = '';
      highlightOverlay.style.backgroundColor = '';
      highlightOverlay.style.left = '';
      highlightOverlay.style.top = '';
      highlightOverlay.style.width = '';
      highlightOverlay.style.height = '';
    }
    
    // Apply new highlights only if there's a matching highlight type
    if (highlight === "zones") {
      zoneElements.forEach(el => {
        if (el) {
          // Remove yellow border, add pulsating class instead
          el.style.outline = '';
          el.style.boxShadow = '';
          el.classList.add('tutorial-zone-pulsate');
        }
      });
    } else if (highlight === "orders") {
      // Function to apply pulsation to minimized order card images
      const applyOrderImagePulsation = () => {
        const orderCarousel = document.querySelector('.order-carousel');
        if (orderCarousel) {
          // Get all order cards that are NOT expanded (minimized view)
          const allOrderCards = orderCarousel.querySelectorAll('.carousel-order-card');
          allOrderCards.forEach(card => {
            // Only pulsate minimized cards (not the expanded center one)
            if (!card.classList.contains('expanded')) {
              card.classList.add('tutorial-order-image-pulsate');
            } else {
              // Remove pulsation from expanded card if it was previously pulsating
              card.classList.remove('tutorial-order-image-pulsate');
            }
          });
        }
      };
      
      // Apply immediately
      applyOrderImagePulsation();
      
      // Poll to keep pulsation on minimized cards (in case carousel updates)
      // Store interval in a variable accessible to cleanup
      const orderPulsationInterval = setInterval(applyOrderImagePulsation, 200);
      
      // Store interval for cleanup
      if (typeof window.tutorialOrderPulsationInterval !== 'undefined') {
        clearInterval(window.tutorialOrderPulsationInterval);
      }
      window.tutorialOrderPulsationInterval = orderPulsationInterval;
    } else if (highlight === "intel") {
      // Apply pulsation to player intel orb and intel bar only (PlayerHUD; not opponent)
      const applyIntelPulsation = () => {
        document.querySelectorAll('.player-avatar-container [data-tutorial-hud="intel-orb"]').forEach((orb) => {
          orb.classList.add('tutorial-intel-orb-pulsate');
        });
        document.querySelectorAll('.player-avatar-container [data-tutorial-hud="intel-bar"]').forEach((bar) => {
          bar.classList.add('tutorial-intel-bar-pulsate');
        });
      };

      // Apply immediately
      applyIntelPulsation();

      // Poll to keep pulsation (in case DOM updates)
      const intelPulsationInterval = setInterval(applyIntelPulsation, 200);

      // Store interval for cleanup
      if (typeof window.tutorialIntelPulsationInterval !== 'undefined') {
        clearInterval(window.tutorialIntelPulsationInterval);
      }
      window.tutorialIntelPulsationInterval = intelPulsationInterval;
    } else if (highlight === "barriers") {
      // Apply pulsation to player barrier sprites (health threshold indicators on PlayerHUD)
      const applyBarriersPulsation = () => {
        document.querySelectorAll('.player-avatar-container [data-tutorial-hud="barrier"]').forEach((el) => {
          el.classList.add('tutorial-barriers-pulsate');
        });
      };

      applyBarriersPulsation();

      const barriersPulsationInterval = setInterval(applyBarriersPulsation, 200);

      if (typeof window.tutorialBarriersPulsationInterval !== 'undefined') {
        clearInterval(window.tutorialBarriersPulsationInterval);
      }
      window.tutorialBarriersPulsationInterval = barriersPulsationInterval;
    } else if (highlight === "health") {
      // Apply pulsation to player health orb and health bar only (PlayerHUD; not opponent)
      const applyHealthPulsation = () => {
        document.querySelectorAll('.player-avatar-container [data-tutorial-hud="health-orb"]').forEach((orb) => {
          orb.classList.add('tutorial-health-orb-pulsate');
        });
        document.querySelectorAll('.player-avatar-container [data-tutorial-hud="health-bar"]').forEach((bar) => {
          bar.classList.add('tutorial-health-bar-pulsate');
        });
      };

      // Apply immediately
      applyHealthPulsation();

      // Poll to keep pulsation (in case DOM updates)
      const healthPulsationInterval = setInterval(applyHealthPulsation, 200);

      // Store interval for cleanup
      if (typeof window.tutorialHealthPulsationInterval !== 'undefined') {
        clearInterval(window.tutorialHealthPulsationInterval);
      }
      window.tutorialHealthPulsationInterval = healthPulsationInterval;
    }
    // If highlight is null or doesn't match any case, all highlights remain cleared
    
    // Cleanup on unmount or highlight change - ensure everything is cleared
    return () => {
      // Clear order pulsation interval if it exists
      if (typeof window.tutorialOrderPulsationInterval !== 'undefined') {
        clearInterval(window.tutorialOrderPulsationInterval);
        delete window.tutorialOrderPulsationInterval;
      }
      
      // Clear order image pulsation
      const orderCarousel = document.querySelector('.order-carousel');
      if (orderCarousel) {
        const allOrderCards = orderCarousel.querySelectorAll('.carousel-order-card');
        allOrderCards.forEach(card => {
          card.classList.remove('tutorial-order-image-pulsate');
        });
      }

      // Clear intel pulsation interval if it exists
      if (typeof window.tutorialIntelPulsationInterval !== 'undefined') {
        clearInterval(window.tutorialIntelPulsationInterval);
        delete window.tutorialIntelPulsationInterval;
      }

      // Clear health pulsation interval if it exists
      if (typeof window.tutorialHealthPulsationInterval !== 'undefined') {
        clearInterval(window.tutorialHealthPulsationInterval);
        delete window.tutorialHealthPulsationInterval;
      }

      // Clear barriers pulsation interval if it exists
      if (typeof window.tutorialBarriersPulsationInterval !== 'undefined') {
        clearInterval(window.tutorialBarriersPulsationInterval);
        delete window.tutorialBarriersPulsationInterval;
      }

      document.querySelectorAll('[data-tutorial-hud="intel-orb"]').forEach((orb) => {
        orb.classList.remove('tutorial-intel-orb-pulsate');
      });
      document.querySelectorAll('[data-tutorial-hud="intel-bar"]').forEach((bar) => {
        bar.classList.remove('tutorial-intel-bar-pulsate');
      });

      document.querySelectorAll('[data-tutorial-hud="health-orb"]').forEach((orb) => {
        orb.classList.remove('tutorial-health-orb-pulsate');
      });
      document.querySelectorAll('[data-tutorial-hud="health-bar"]').forEach((bar) => {
        bar.classList.remove('tutorial-health-bar-pulsate');
      });

      document.querySelectorAll('[data-tutorial-hud="barrier"]').forEach((el) => {
        el.classList.remove('tutorial-barriers-pulsate');
      });
      
      zoneElements.forEach(el => {
        if (el) {
          el.style.outline = '';
          el.style.boxShadow = '';
          el.classList.remove('tutorial-zone-pulsate');
        }
      });
      if (highlightOverlay) {
        highlightOverlay.style.display = 'none';
        highlightOverlay.style.outline = '';
        highlightOverlay.style.boxShadow = '';
        highlightOverlay.style.backgroundColor = '';
        highlightOverlay.style.left = '';
        highlightOverlay.style.top = '';
        highlightOverlay.style.width = '';
        highlightOverlay.style.height = '';
      }
    };
  }, [currentTutorial]); // Depend on entire currentTutorial object to ensure effect runs when tutorial changes, even if highlight value is the same

  // Cleanup: Remove any existing order blocking overlays on mount
  useEffect(() => {
    // Remove left and right blocking overlays if they exist
    const leftBlockOverlay = document.getElementById('orders-block-overlay-left');
    const rightBlockOverlay = document.getElementById('orders-block-overlay-right');
    if (leftBlockOverlay) {
      leftBlockOverlay.remove();
    }
    if (rightBlockOverlay) {
      rightBlockOverlay.remove();
    }
  }, []); // Run once on mount

  // Block Forsaken Sentinel (order 42) as a target during "Opponent's turn" tutorial
  useEffect(() => {
    // ✅ CRITICAL: If no tutorial is active, remove blocking overlay and return early
    if (!currentTutorial) {
      const forsakenSentinelBlockOverlay = document.getElementById('forsaken-sentinel-block-overlay');
      if (forsakenSentinelBlockOverlay) {
        forsakenSentinelBlockOverlay.style.display = 'none';
        forsakenSentinelBlockOverlay.remove();
      }
      return;
    }
    
    const isOpponentTurnTutorial = currentTutorial?.stepId === 'opponents_turn';
    const isDefeatTutorial = currentTutorial?.stepId === 'defeat_your_opponent';
    const isTutorialComplete = !currentTutorial || isDefeatTutorial; // After final tutorial, unlock everything
    const shouldBlockForsakenSentinel = !isTutorialComplete && isOpponentTurnTutorial;
    
    // Get or create blocking overlay for Forsaken Sentinel
    let forsakenSentinelBlockOverlay = document.getElementById('forsaken-sentinel-block-overlay');
    if (!forsakenSentinelBlockOverlay) {
      forsakenSentinelBlockOverlay = document.createElement('div');
      forsakenSentinelBlockOverlay.id = 'forsaken-sentinel-block-overlay';
      forsakenSentinelBlockOverlay.style.position = 'fixed';
      forsakenSentinelBlockOverlay.style.zIndex = '99999';
      forsakenSentinelBlockOverlay.style.pointerEvents = 'auto';
      forsakenSentinelBlockOverlay.style.backgroundColor = 'transparent';
      document.body.appendChild(forsakenSentinelBlockOverlay);
    }
    
    const updateOverlay = () => {
      if (shouldBlockForsakenSentinel && forsakenSentinelBlockOverlay) {
        // Find opponent's front zone - same pattern as zone blocking
        const opponentFrontZone = document.querySelector('.battle-zone.opponent-front');
        if (opponentFrontZone) {
          // Check if zone contains Forsaken Sentinel
          const zoneText = opponentFrontZone.textContent || '';
          if (zoneText.includes('Forsaken Sentinel') || zoneText.includes('Sentinel')) {
            const rect = opponentFrontZone.getBoundingClientRect();
            forsakenSentinelBlockOverlay.style.display = 'block';
            forsakenSentinelBlockOverlay.style.left = `${rect.left}px`;
            forsakenSentinelBlockOverlay.style.top = `${rect.top}px`;
            forsakenSentinelBlockOverlay.style.width = `${rect.width}px`;
            forsakenSentinelBlockOverlay.style.height = `${rect.height}px`;
          } else {
            forsakenSentinelBlockOverlay.style.display = 'none';
          }
        } else {
          forsakenSentinelBlockOverlay.style.display = 'none';
        }
      } else {
        // Explicitly hide overlay when not in "Opponent's turn" tutorial
        if (forsakenSentinelBlockOverlay) {
          forsakenSentinelBlockOverlay.style.display = 'none';
        }
      }
    };
    
    // Update immediately
    updateOverlay();
    
    // Continuously update overlay position when blocking (in case unit moves or isn't rendered yet)
    let updateInterval = null;
    if (shouldBlockForsakenSentinel) {
      updateInterval = setInterval(updateOverlay, 100); // Update every 100ms
    }
    
    // Also update on window resize/scroll
    const handleResize = () => {
      updateOverlay();
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    
    // Cleanup on unmount or tutorial change
    return () => {
      if (updateInterval) {
        clearInterval(updateInterval);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
      // Explicitly hide overlay on cleanup
      if (forsakenSentinelBlockOverlay) {
        forsakenSentinelBlockOverlay.style.display = 'none';
      }
    };
  }, [currentTutorial]);

  // Block zones: both zones before deploy tutorial, only flank during deploy tutorial, then unblock both
  useEffect(() => {
    // ✅ CRITICAL: If no tutorial is active, remove all blocking overlays and return early
    if (!currentTutorial) {
      const frontBlockOverlay = document.getElementById('front-block-overlay');
      const flankBlockOverlay = document.getElementById('flank-block-overlay');
      if (frontBlockOverlay) {
        frontBlockOverlay.style.display = 'none';
        frontBlockOverlay.remove();
      }
      if (flankBlockOverlay) {
        flankBlockOverlay.style.display = 'none';
        flankBlockOverlay.remove();
      }
      return;
    }

    // Mission 4 uses its own zone blocking logic (t4-front/flank-block-overlay)
    if (missionId === 'chapter_1_mission_4') {
      const frontBlockOverlay = document.getElementById('front-block-overlay');
      const flankBlockOverlay = document.getElementById('flank-block-overlay');
      if (frontBlockOverlay) { frontBlockOverlay.style.display = 'none'; frontBlockOverlay.remove(); }
      if (flankBlockOverlay) { flankBlockOverlay.style.display = 'none'; flankBlockOverlay.remove(); }
      return;
    }

    // Mission 3: Block flank during "Destroy the Drone" / "Deploy Effects" so Kek must deploy to front
    // Block front during suppressTutorialDialog so Missile Barrage must go to flank
    // Block front during "Protect Your Units" so Disruptor must deploy to flank only
    if (missionId === 'chapter_1_mission_3') {
      const isDestroyTheDroneTutorial = currentTutorial?.stepId === 'destroy_the_drone';
      const isDeployEffectsTutorial = currentTutorial?.stepId === 'deploy_effects';
      const isSamuelJohnsonTutorial = currentTutorial?.stepId === 'samuel_johnson';
      const shouldBlockFlankForKek = isDestroyTheDroneTutorial || isDeployEffectsTutorial || isSamuelJohnsonTutorial;
      const isMissileBarrageDialogVisible = currentTutorial?.stepId === 'missile_barrage' && !suppressTutorialDialog;
      const shouldBlockBothForMissileBarrageDialog = isMissileBarrageDialogVisible;
      const shouldBlockFrontForMissileBarrage = suppressTutorialDialog;

      let frontBlockOverlay = document.getElementById('front-block-overlay');
      let flankBlockOverlay = document.getElementById('flank-block-overlay');
      if (!frontBlockOverlay) {
        frontBlockOverlay = document.createElement('div');
        frontBlockOverlay.id = 'front-block-overlay';
        frontBlockOverlay.style.position = 'fixed';
        frontBlockOverlay.style.zIndex = '99999';
        frontBlockOverlay.style.pointerEvents = 'auto';
        frontBlockOverlay.style.backgroundColor = 'transparent';
        document.body.appendChild(frontBlockOverlay);
      }
      if (!flankBlockOverlay) {
        flankBlockOverlay = document.createElement('div');
        flankBlockOverlay.id = 'flank-block-overlay';
        flankBlockOverlay.style.position = 'fixed';
        flankBlockOverlay.style.zIndex = '99999';
        flankBlockOverlay.style.pointerEvents = 'auto';
        flankBlockOverlay.style.backgroundColor = 'transparent';
        document.body.appendChild(flankBlockOverlay);
      }

      if (shouldBlockFlankForKek) {
        frontBlockOverlay.style.display = 'none';
        const flankZone = document.querySelector('.battle-zone.flank');
        if (flankZone) {
          const rect = flankZone.getBoundingClientRect();
          flankBlockOverlay.style.display = 'block';
          flankBlockOverlay.style.left = `${rect.left}px`;
          flankBlockOverlay.style.top = `${rect.top}px`;
          flankBlockOverlay.style.width = `${rect.width}px`;
          flankBlockOverlay.style.height = `${rect.height}px`;
        } else {
          flankBlockOverlay.style.display = 'none';
        }
        const updateM3Overlays = () => {
          const flank = document.querySelector('.battle-zone.flank');
          if (flank && currentTutorial?.stepId && ['destroy_the_drone', 'deploy_effects', 'samuel_johnson'].includes(currentTutorial.stepId)) {
            const r = flank.getBoundingClientRect();
            flankBlockOverlay.style.left = `${r.left}px`;
            flankBlockOverlay.style.top = `${r.top}px`;
            flankBlockOverlay.style.width = `${r.width}px`;
            flankBlockOverlay.style.height = `${r.height}px`;
          }
        };
        const m3Interval = setInterval(updateM3Overlays, 100);
        const handleM3Resize = () => updateM3Overlays();
        window.addEventListener('resize', handleM3Resize);
        window.addEventListener('scroll', handleM3Resize);
        return () => {
          clearInterval(m3Interval);
          window.removeEventListener('resize', handleM3Resize);
          window.removeEventListener('scroll', handleM3Resize);
          frontBlockOverlay.style.display = 'none';
          flankBlockOverlay.style.display = 'none';
        };
      } else if (shouldBlockBothForMissileBarrageDialog || shouldBlockFrontForMissileBarrage) {
        let opponentFrontBlockOverlay = document.getElementById('opponent-front-block-overlay');
        if (!opponentFrontBlockOverlay) {
          opponentFrontBlockOverlay = document.createElement('div');
          opponentFrontBlockOverlay.id = 'opponent-front-block-overlay';
          opponentFrontBlockOverlay.style.position = 'fixed';
          opponentFrontBlockOverlay.style.zIndex = '99999';
          opponentFrontBlockOverlay.style.pointerEvents = 'auto';
          opponentFrontBlockOverlay.style.backgroundColor = 'transparent';
          document.body.appendChild(opponentFrontBlockOverlay);
        }
        const updateZoneOverlays = () => {
          const front = document.querySelector('.battle-zone.front');
          const opponentFront = document.querySelector('.battle-zone.opponent-front');
          const flank = document.querySelector('.battle-zone.flank');
          if (front) {
            const r = front.getBoundingClientRect();
            frontBlockOverlay.style.display = 'block';
            frontBlockOverlay.style.left = `${r.left}px`;
            frontBlockOverlay.style.top = `${r.top}px`;
            frontBlockOverlay.style.width = `${r.width}px`;
            frontBlockOverlay.style.height = `${r.height}px`;
          }
          if (opponentFront) {
            const r = opponentFront.getBoundingClientRect();
            opponentFrontBlockOverlay.style.display = 'block';
            opponentFrontBlockOverlay.style.left = `${r.left}px`;
            opponentFrontBlockOverlay.style.top = `${r.top}px`;
            opponentFrontBlockOverlay.style.width = `${r.width}px`;
            opponentFrontBlockOverlay.style.height = `${r.height}px`;
          }
          if (shouldBlockBothForMissileBarrageDialog && flank) {
            const r = flank.getBoundingClientRect();
            flankBlockOverlay.style.display = 'block';
            flankBlockOverlay.style.left = `${r.left}px`;
            flankBlockOverlay.style.top = `${r.top}px`;
            flankBlockOverlay.style.width = `${r.width}px`;
            flankBlockOverlay.style.height = `${r.height}px`;
          } else {
            flankBlockOverlay.style.display = 'none';
          }
        };
        updateZoneOverlays();
        const m3Interval = setInterval(updateZoneOverlays, 100);
        const handleResize = () => updateZoneOverlays();
        window.addEventListener('resize', handleResize);
        window.addEventListener('scroll', handleResize);
        return () => {
          clearInterval(m3Interval);
          window.removeEventListener('resize', handleResize);
          window.removeEventListener('scroll', handleResize);
          frontBlockOverlay.style.display = 'none';
          flankBlockOverlay.style.display = 'none';
          opponentFrontBlockOverlay.style.display = 'none';
        };
      } else {
        frontBlockOverlay.style.display = 'none';
        flankBlockOverlay.style.display = 'none';
        return;
      }
    }
    
    const isDeployTutorial = currentTutorial?.stepId === 'deploy';
    const isStratagemsTutorial = currentTutorial?.stepId === 'stratagems';
    const isEndTurnTutorial = (currentTutorial?.stepId === 'end_turn_1' || currentTutorial?.stepId === 'end_turn_2');
    const isAttackingTutorial = currentTutorial?.stepId === 'out_of_reach';
    const isOpponentTurnTutorial = currentTutorial?.stepId === 'opponents_turn';
    const isSpecialUnitsTutorial = currentTutorial?.stepId === 'special_units';
    const isPreparingTutorial = currentTutorial?.stepId === 'preparing';
    const isOverwhelmingTutorial = currentTutorial?.stepId === 'overwhelming_force';
    const isCounterStrikeTutorial = currentTutorial?.stepId === 'counter_strike';
    const isStrikeTheBerserkerTutorial = currentTutorial?.stepId === 'strike_the_berserker';
    const isDefeatTutorial = currentTutorial?.stepId === 'defeat_your_opponent';
    // Mission 2 tutorial titles
    const isDeployYourForcesTutorial = currentTutorial?.stepId === 'deploy_your_forces';
    const isInfernoDropTutorial = currentTutorial?.stepId === 'inferno_drop';
    const isShieldAndAttackTutorial = currentTutorial?.stepId === 'shield_and_attack';
    const isDuplicateTutorial = currentTutorial?.stepId === 'duplicate';
    const isKillThreatsTutorial = currentTutorial?.stepId === 'kill_threats';
    const isFinalBlowTutorial = currentTutorial?.stepId === 'final_blow';
    const isChiefEngineerDestroyedTutorial = currentTutorial?.stepId === 'chief_engineer_destroyed';
    const isTutorialComplete = !currentTutorial || isDefeatTutorial; // After final tutorial, unlock everything
    
    // Block both zones BEFORE deploy tutorial
    // Block only flank DURING deploy tutorial (front should be unblocked)
    // Block only front DURING Special Units (deploy Opportunist to flank only) and overwhelming force tutorial
    // Unblock both zones after deploy tutorial and during attacking tutorial and preparing tutorial
    // Unblock everything after final tutorial
    const isOutOfReachTutorial = currentTutorial?.stepId === 'out_of_reach';
    const isAttackTheGeneralTutorial = currentTutorial?.stepId === 'attack_the_general';
    const isFinishThemTutorial = currentTutorial?.stepId === 'finish_them';
    // Mission 2: unblock zones during stratagem/attack tutorials so player can interact
    const shouldBlockBothZones = !isTutorialComplete && !isDeployTutorial && !isStratagemsTutorial && !isEndTurnTutorial && !isAttackingTutorial && !isOpponentTurnTutorial && !isSpecialUnitsTutorial && !isPreparingTutorial && !isOverwhelmingTutorial && !isCounterStrikeTutorial && !isStrikeTheBerserkerTutorial && !isOutOfReachTutorial && !isAttackTheGeneralTutorial && !isFinishThemTutorial && !isDeployYourForcesTutorial && !isInfernoDropTutorial && !isShieldAndAttackTutorial && !isDuplicateTutorial && !isKillThreatsTutorial && !isFinalBlowTutorial;
    const shouldBlockFlankOnly = !isTutorialComplete && (isDeployTutorial || isDeployYourForcesTutorial); // Block only flank during deploy tutorials (front only)
    const shouldBlockFrontOnly = !isTutorialComplete && (isOpponentTurnTutorial || isOverwhelmingTutorial || isSpecialUnitsTutorial); // Block front: deploy to flank only
    const isDuplicateNeedsDynamicFrontBlock = isDuplicateTutorial; // Duplicate tutorial: block front only AFTER targeting (when tank copy is in carousel)
    
    // ✅ CRITICAL: Double-check currentTutorial is still valid before creating overlays
    // This prevents race conditions where tutorial ends between the early return and here
    if (!currentTutorial) {
      return;
    }
    
    // Get or create blocking overlay elements
    let frontBlockOverlay = document.getElementById('front-block-overlay');
    if (!frontBlockOverlay) {
      frontBlockOverlay = document.createElement('div');
      frontBlockOverlay.id = 'front-block-overlay';
      frontBlockOverlay.style.position = 'fixed';
      frontBlockOverlay.style.zIndex = '99999';
      frontBlockOverlay.style.pointerEvents = 'auto';
      frontBlockOverlay.style.backgroundColor = 'transparent';
      document.body.appendChild(frontBlockOverlay);
    }
    
    let flankBlockOverlay = document.getElementById('flank-block-overlay');
    if (!flankBlockOverlay) {
      flankBlockOverlay = document.createElement('div');
      flankBlockOverlay.id = 'flank-block-overlay';
      flankBlockOverlay.style.position = 'fixed';
      flankBlockOverlay.style.zIndex = '99999';
      flankBlockOverlay.style.pointerEvents = 'auto';
      flankBlockOverlay.style.backgroundColor = 'transparent';
      document.body.appendChild(flankBlockOverlay);
    }
    
    const updateOverlays = () => {
      // Dynamic front-block for Duplicate tutorial: only block front once the duplicated tank is in hand (carousel)
      let dynamicBlockFront = false;
      if (isDuplicateNeedsDynamicFrontBlock) {
        const carouselCards = document.querySelectorAll('.carousel-order-card');
        for (const card of carouselCards) {
          const text = card.textContent || '';
          if (text.includes('Viper Tank') || text.includes('Steel Viper')) {
            dynamicBlockFront = true;
            break;
          }
        }
      }
      const effectiveBlockFront = shouldBlockFrontOnly || dynamicBlockFront;

      if (shouldBlockBothZones) {
        // Block both zones (before deploy tutorial)
        const frontZone = document.querySelector('.battle-zone.front');
        if (frontZone) {
          const rect = frontZone.getBoundingClientRect();
          frontBlockOverlay.style.display = 'block';
          frontBlockOverlay.style.left = `${rect.left}px`;
          frontBlockOverlay.style.top = `${rect.top}px`;
          frontBlockOverlay.style.width = `${rect.width}px`;
          frontBlockOverlay.style.height = `${rect.height}px`;
        } else {
          frontBlockOverlay.style.display = 'none';
        }
        
        const flankZone = document.querySelector('.battle-zone.flank');
        if (flankZone) {
          const rect = flankZone.getBoundingClientRect();
          flankBlockOverlay.style.display = 'block';
          flankBlockOverlay.style.left = `${rect.left}px`;
          flankBlockOverlay.style.top = `${rect.top}px`;
          flankBlockOverlay.style.width = `${rect.width}px`;
          flankBlockOverlay.style.height = `${rect.height}px`;
        } else {
          flankBlockOverlay.style.display = 'none';
        }
      } else if (shouldBlockFlankOnly) {
        // Block only flank during deploy tutorial (front should be unblocked)
        frontBlockOverlay.style.display = 'none';
        
        const flankZone = document.querySelector('.battle-zone.flank');
        if (flankZone) {
          const rect = flankZone.getBoundingClientRect();
          flankBlockOverlay.style.display = 'block';
          flankBlockOverlay.style.left = `${rect.left}px`;
          flankBlockOverlay.style.top = `${rect.top}px`;
          flankBlockOverlay.style.width = `${rect.width}px`;
          flankBlockOverlay.style.height = `${rect.height}px`;
        } else {
          flankBlockOverlay.style.display = 'none';
        }
      } else if (effectiveBlockFront) {
        // Block only front (deploy to flank only) - use player's front zone
        flankBlockOverlay.style.display = 'none';
        
        const frontZones = document.querySelectorAll('.battle-zone.front');
        const playerFrontZone = Array.from(frontZones).find(z => !z.classList.contains('opponent-front')) || frontZones[0];
        if (playerFrontZone) {
          const rect = playerFrontZone.getBoundingClientRect();
          frontBlockOverlay.style.display = 'block';
          frontBlockOverlay.style.left = `${rect.left}px`;
          frontBlockOverlay.style.top = `${rect.top}px`;
          frontBlockOverlay.style.width = `${rect.width}px`;
          frontBlockOverlay.style.height = `${rect.height}px`;
        } else {
          frontBlockOverlay.style.display = 'none';
        }
      } else {
        // Hide both blocking overlays (after deploy tutorial)
        frontBlockOverlay.style.display = 'none';
        flankBlockOverlay.style.display = 'none';
      }
    };
    
    // Update immediately
    updateOverlays();
    
    // Continuously update overlay positions when blocking (in case zones move or aren't rendered yet)
    let updateInterval = null;
    if (shouldBlockBothZones || shouldBlockFlankOnly || shouldBlockFrontOnly || isDuplicateNeedsDynamicFrontBlock) {
      updateInterval = setInterval(updateOverlays, 100);
    }
    
    const handleResize = () => {
      if (shouldBlockBothZones || shouldBlockFlankOnly || shouldBlockFrontOnly || isDuplicateNeedsDynamicFrontBlock) {
        updateOverlays();
      }
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    
    // Cleanup on unmount
    return () => {
      if (updateInterval) {
        clearInterval(updateInterval);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
      if (frontBlockOverlay) {
        frontBlockOverlay.style.display = 'none';
      }
      if (flankBlockOverlay) {
        flankBlockOverlay.style.display = 'none';
      }
    };
  }, [currentTutorial, suppressTutorialDialog]);

  // Block end turn button until "End turn" tutorial shows (allow it during any "End turn" tutorial)
  useEffect(() => {
    // Mission 5 is unrestricted free-play: never block end turn in UI.
    if (missionId === 'chapter_1_mission_5') {
      const endTurnBlockOverlay = document.getElementById('end-turn-block-overlay');
      if (endTurnBlockOverlay) {
        endTurnBlockOverlay.style.display = 'none';
        endTurnBlockOverlay.remove();
      }
      const overlayEndTurnBlock = document.getElementById('overlay-end-turn-block');
      if (overlayEndTurnBlock) {
        overlayEndTurnBlock.style.display = 'none';
        overlayEndTurnBlock.remove();
      }
      return;
    }

    // ✅ CRITICAL: If no tutorial is active, remove blocking overlay and return early
    if (!currentTutorial) {
      const endTurnBlockOverlay = document.getElementById('end-turn-block-overlay');
      if (endTurnBlockOverlay) {
        endTurnBlockOverlay.style.display = 'none';
        endTurnBlockOverlay.remove();
      }
      return;
    }

    // Missions 3 & 4 use their own end-turn blocking logic
    if (missionId === 'chapter_1_mission_3' || missionId === 'chapter_1_mission_4') {
      const endTurnBlockOverlay = document.getElementById('end-turn-block-overlay');
      if (endTurnBlockOverlay) { endTurnBlockOverlay.style.display = 'none'; endTurnBlockOverlay.remove(); }
      return;
    }

    // Tutorial overlay config drives this step: generic overlay is handled in overlay-specific effect below
    if (gameState?.scriptState?.tutorialOverlay && overlayStepConfig) {
      const endTurnBlockOverlay = document.getElementById('end-turn-block-overlay');
      if (endTurnBlockOverlay) { endTurnBlockOverlay.style.display = 'none'; endTurnBlockOverlay.remove(); }
      return;
    }
    
    const isEndTurnTutorial = (currentTutorial?.stepId === 'end_turn_1' || currentTutorial?.stepId === 'end_turn_2');
    const isPreparingTutorial = currentTutorial?.stepId === 'preparing';
    const isSpecialUnitsTutorial = currentTutorial?.stepId === 'special_units';
    const isOverwhelmingTutorial = currentTutorial?.stepId === 'overwhelming_force';
    const isDefeatTutorial = currentTutorial?.stepId === 'defeat_your_opponent';
    const isDeployYourForces = currentTutorial?.stepId === 'deploy_your_forces';
    const isShieldAndAttack = currentTutorial?.stepId === 'shield_and_attack';
    const isKillThreats = currentTutorial?.stepId === 'kill_threats';
    const isAttackTheGeneralTutorial = currentTutorial?.stepId === 'attack_the_general';
    const isTutorialComplete = !currentTutorial || isDefeatTutorial; // After final tutorial, unlock everything
    
    // For "Overwhelming force" tutorial, check if David is deployed to flank
    let davidInFlank = false;
    if (isOverwhelmingTutorial) {
      const flankZone = document.querySelector('.battle-zone.flank');
      if (flankZone) {
        davidInFlank = flankZone.textContent?.includes('David') || false;
      }
    }

    // Mission 2: check conditions for unblocking end turn
    let m2DeployDone = false;
    if (isDeployYourForces) {
      const frontZone = document.querySelector('.battle-zone.front');
      if (frontZone) {
        const text = frontZone.textContent || '';
        m2DeployDone = text.includes('Chief Engineer') && text.includes('Youthful Patriot');
      }
    }
    const shouldBlockButton = !isTutorialComplete && !isEndTurnTutorial && !isPreparingTutorial && !isSpecialUnitsTutorial && (!isOverwhelmingTutorial || !davidInFlank) && (!isDeployYourForces || !m2DeployDone) && (!isAttackTheGeneralTutorial || !opportunist302AllowsPassTurnDuringAttackGeneralStep(gameState, userId));
    
    // Get or create end turn button blocking overlay
    let endTurnBlockOverlay = document.getElementById('end-turn-block-overlay');
    if (!endTurnBlockOverlay) {
      endTurnBlockOverlay = document.createElement('div');
      endTurnBlockOverlay.id = 'end-turn-block-overlay';
      endTurnBlockOverlay.style.position = 'fixed';
      endTurnBlockOverlay.style.zIndex = '100001'; // Higher than button's z-index (1000)
      endTurnBlockOverlay.style.pointerEvents = 'auto';
      endTurnBlockOverlay.style.backgroundColor = 'transparent';
      document.body.appendChild(endTurnBlockOverlay);
    }
    
    const updateOverlay = () => {
      // Re-check David in flank for "Overwhelming force" tutorial (in case it was just deployed)
      let currentDavidInFlank = false;
      if (isOverwhelmingTutorial) {
        const flankZone = document.querySelector('.battle-zone.flank');
        if (flankZone) {
          currentDavidInFlank = flankZone.textContent?.includes('David') || false;
        }
      }

      // Re-check Mission 2 conditions dynamically
      let currentM2DeployDone = false;
      if (isDeployYourForces) {
        const frontZone = document.querySelector('.battle-zone.front');
        if (frontZone) {
          const text = frontZone.textContent || '';
          currentM2DeployDone = text.includes('Chief Engineer') && text.includes('Youthful Patriot');
        }
      }
      // Kill threats: unblock end turn when golden border appears (no more attackable units)
      let currentM2ShieldAttackDone = false;
      if (isKillThreats) {
        const endBtn = document.querySelector('.end-turn-button');
        if (endBtn) {
          const borderColor = window.getComputedStyle(endBtn).borderColor;
          currentM2ShieldAttackDone = borderColor.includes('255, 215, 0');
        }
      }
      
      // Recalculate shouldBlockButton with current state
      const currentShouldBlockButton = !isTutorialComplete && !isEndTurnTutorial && !isPreparingTutorial && !isSpecialUnitsTutorial && (!isOverwhelmingTutorial || !currentDavidInFlank) && (!isDeployYourForces || !currentM2DeployDone) && (!isShieldAndAttack || !currentM2ShieldAttackDone) && (!isKillThreats || !currentM2ShieldAttackDone) && (!isAttackTheGeneralTutorial || !opportunist302AllowsPassTurnDuringAttackGeneralStep(gameState, userId));
      
      if (currentShouldBlockButton) {
        // Find the end turn button
        const endTurnButton = document.querySelector('.end-turn-button');
        if (endTurnButton) {
          const rect = endTurnButton.getBoundingClientRect();
          endTurnBlockOverlay.style.display = 'block';
          endTurnBlockOverlay.style.left = `${rect.left}px`;
          endTurnBlockOverlay.style.top = `${rect.top}px`;
          endTurnBlockOverlay.style.width = `${rect.width}px`;
          endTurnBlockOverlay.style.height = `${rect.height}px`;
        } else {
          endTurnBlockOverlay.style.display = 'none';
        }
      } else {
        // Hide blocking overlay when button should be unblocked
        endTurnBlockOverlay.style.display = 'none';
      }
    };
    
    // Update immediately
    updateOverlay();
    
    const needsPolling = shouldBlockButton || isOverwhelmingTutorial || isDeployYourForces || isShieldAndAttack || isKillThreats || isAttackTheGeneralTutorial;
    let updateInterval = null;
    if (needsPolling) {
      updateInterval = setInterval(updateOverlay, 100);
    }
    
    const handleResize = () => {
      if (needsPolling) {
        updateOverlay();
      }
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    
    // Cleanup on unmount
    return () => {
      if (updateInterval) {
        clearInterval(updateInterval);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
      if (endTurnBlockOverlay) {
        endTurnBlockOverlay.style.display = 'none';
      }
    };
  }, [missionId, currentTutorial, gameState?.scriptState?.tutorialOverlay, overlayStepConfig, gameState?.state, gameState?.turnNumber, userId]);

  // Tutorial overlay config: end-turn button (when overlay defines endTurnActive for current step)
  useEffect(() => {
    if (missionId === 'chapter_1_mission_5') {
      const overlayId = 'overlay-end-turn-block';
      const el = document.getElementById(overlayId);
      if (el) {
        el.style.display = 'none';
        el.remove();
      }
      return;
    }
    if (!overlayStepConfig) return;
    let canEnd = overlayStepConfig.endTurnActive === true;
    // Mission 3 "Samuel Johnson": allow end turn only after Samuel (386) has been deployed
    if (missionId === 'chapter_1_mission_3' && currentTutorial?.stepId === 'samuel_johnson') {
      const playerState = gameState?.state?.[userId];
      const front = playerState?.playedOrders?.front || [];
      const flank = playerState?.playedOrders?.flank || [];
      canEnd = [...front, ...flank].some(u => u && (Number(u.id) === 386 || Number(u.orderId) === 386));
    }
    if (missionId === 'chapter_1_mission_1' && currentTutorial?.stepId === 'attack_the_general') {
      canEnd = opportunist302AllowsPassTurnDuringAttackGeneralStep(gameState, userId);
    }
    const overlayId = 'overlay-end-turn-block';
    if (canEnd) {
      const el = document.getElementById(overlayId);
      if (el) { el.style.display = 'none'; el.remove(); }
      const enableBtn = () => {
        const btn = document.querySelector('.end-turn-button');
        if (btn && btn.disabled) btn.disabled = false;
      };
      enableBtn();
      const iv = setInterval(enableBtn, 200);
      return () => clearInterval(iv);
    }
    let ovr = document.getElementById(overlayId);
    if (!ovr) {
      ovr = document.createElement('div');
      ovr.id = overlayId;
      ovr.style.cssText = 'position:fixed;z-index:100001;pointer-events:auto;background:transparent;';
      document.body.appendChild(ovr);
    }
    const update = () => {
      const btn = document.querySelector('.end-turn-button');
      if (btn) {
        const rect = btn.getBoundingClientRect();
        ovr.style.display = 'block';
        ovr.style.left = `${rect.left}px`;
        ovr.style.top = `${rect.top}px`;
        ovr.style.width = `${rect.width}px`;
        ovr.style.height = `${rect.height}px`;
      } else {
        ovr.style.display = 'none';
      }
    };
    update();
    const iv = setInterval(update, 100);
    return () => {
      clearInterval(iv);
      if (ovr) { ovr.style.display = 'none'; ovr.remove(); }
    };
  }, [overlayStepConfig, missionId, currentTutorial?.stepId, currentTutorial?.title, gameState?.state, gameState?.turnNumber, userId]);

  // Tutorial overlay config: target display (hide highlights) and targeting restrictions for isTarget.js
  const OVERLAY_HIDE_ATTRS = ['data-tutorial-hide-zone-front', 'data-tutorial-hide-zone-flank', 'data-tutorial-hide-opponent-general', 'data-tutorial-hide-friendly-general'];
  useEffect(() => {
    const body = typeof document !== 'undefined' ? document.body : null;
    if (!body) return;
    if (!overlayStepConfig) {
      OVERLAY_HIDE_ATTRS.forEach(attr => body.removeAttribute(attr));
      body.removeAttribute('data-tutorial-overlay-targeting');
      body.removeAttribute('data-tutorial-overlay-strategem-restrict');
      body.removeAttribute('data-tutorial-overlay-weapon-attach-restrict');
      body.removeAttribute('data-tutorial-overlay-attack-require-weapon-attached');
      body.removeAttribute('data-tutorial-overlay-disable-cancel-targeting');
      body.removeAttribute('data-tutorial-overlay-deploy-no-targeting');
      body.removeAttribute('data-tutorial-overlay-deploy-hide-zone-front');
      const toRemove = [];
      for (let i = 0; i < body.attributes.length; i++) {
        const name = body.attributes[i].name;
        if (name.startsWith('data-tutorial-hide-opponent-unit-') || name.startsWith('data-tutorial-hide-friendly-unit-')) toRemove.push(name);
      }
      toRemove.forEach(name => body.removeAttribute(name));
      return;
    }
    // Reset zone/unit hide flags each step so earlier steps (e.g. deploy: hide flank) do not persist.
    // Without this, data-tutorial-hide-zone-flank stays true through special_units → out_of_reach and
    // isTarget.js rejects opponent flank during Guided Missile (Panicked Overseer), unless every bypass matches.
    OVERLAY_HIDE_ATTRS.forEach(attr => body.removeAttribute(attr));
    const unitHideToRemove = [];
    for (let i = 0; i < body.attributes.length; i++) {
      const name = body.attributes[i].name;
      if (name.startsWith('data-tutorial-hide-opponent-unit-') || name.startsWith('data-tutorial-hide-friendly-unit-')) unitHideToRemove.push(name);
    }
    unitHideToRemove.forEach(name => body.removeAttribute(name));

    const targetDisplay = overlayStepConfig.targetDisplay;
    if (Array.isArray(targetDisplay)) {
      targetDisplay.filter(t => t && t.hidden === true).forEach(t => {
        if (t.type === 'zone_front') body.setAttribute('data-tutorial-hide-zone-front', 'true');
        else if (t.type === 'zone_flank') body.setAttribute('data-tutorial-hide-zone-flank', 'true');
        else if (t.type === 'opponent_general') body.setAttribute('data-tutorial-hide-opponent-general', 'true');
        else if (t.type === 'friendly_general') body.setAttribute('data-tutorial-hide-friendly-general', 'true');
        else if (t.type === 'opponent_unit' && typeof t.unitId === 'number') body.setAttribute(`data-tutorial-hide-opponent-unit-${t.unitId}`, 'true');
        else if (t.type === 'friendly_unit' && typeof t.unitId === 'number') body.setAttribute(`data-tutorial-hide-friendly-unit-${t.unitId}`, 'true');
      });
    }
    // Mission 3: after Missile Barrage message closes (Continue), flank must be targetable by Missile Barrage
    if (missionId === 'chapter_1_mission_3' && currentTutorial?.stepId === 'missile_barrage' && suppressTutorialDialog) {
      body.removeAttribute('data-tutorial-hide-zone-flank');
    }
    const attackable = overlayStepConfig.attackableOpponentZone;
    const clickableFriendly = overlayStepConfig.clickableFriendlyZone;
    if (Array.isArray(attackable) || Array.isArray(clickableFriendly)) {
      body.setAttribute('data-tutorial-overlay-targeting', JSON.stringify({
        attackableOpponentZone: attackable || null,
        clickableFriendlyZone: clickableFriendly || null
      }));
    } else {
      body.removeAttribute('data-tutorial-overlay-targeting');
    }
    const strategemAllow = overlayStepConfig.strategemTargetAllowOnly;
    if (strategemAllow && typeof strategemAllow === 'object' && Object.keys(strategemAllow).length > 0) {
      body.setAttribute('data-tutorial-overlay-strategem-restrict', JSON.stringify(strategemAllow));
    } else {
      body.removeAttribute('data-tutorial-overlay-strategem-restrict');
    }
    const weaponAttachAllowed = overlayStepConfig.allowedWeaponAttachTargetOrderIds;
    if (Array.isArray(weaponAttachAllowed) && weaponAttachAllowed.length > 0) {
      body.setAttribute('data-tutorial-overlay-weapon-attach-restrict', JSON.stringify(weaponAttachAllowed));
    } else {
      body.removeAttribute('data-tutorial-overlay-weapon-attach-restrict');
    }
    const attackRequireWeapon = overlayStepConfig.allowedAttackersRequireWeaponAttached;
    if (Array.isArray(attackRequireWeapon) && attackRequireWeapon.length > 0) {
      body.setAttribute('data-tutorial-overlay-attack-require-weapon-attached', JSON.stringify(attackRequireWeapon));
    } else {
      body.removeAttribute('data-tutorial-overlay-attack-require-weapon-attached');
    }
    // Overlay attack restrict (e.g. Kill threats): restrict targets and optionally attackers (client)
    const overlayAttackers = overlayStepConfig.allowedAttackers;
    const overlayTargets = overlayStepConfig.allowedAttackTargets;
    const hasTargetRestrict = Array.isArray(overlayTargets) && overlayTargets.length > 0;
    const hasAttackerRestrict = Array.isArray(overlayAttackers) && overlayAttackers.length > 0;
    if (hasTargetRestrict || hasAttackerRestrict) {
      const payload = {};
      if (hasAttackerRestrict) payload.attackers = overlayAttackers;
      if (hasTargetRestrict) payload.targets = overlayTargets;
      body.setAttribute('data-tutorial-overlay-attack-restrict', JSON.stringify(payload));
    } else {
      body.removeAttribute('data-tutorial-overlay-attack-restrict');
    }
    if (overlayStepConfig.disableCancelTargeting === true) {
      body.setAttribute('data-tutorial-overlay-disable-cancel-targeting', 'true');
    } else {
      body.removeAttribute('data-tutorial-overlay-disable-cancel-targeting');
    }
    const deployNoTargeting = overlayStepConfig.deployOrdersNoTargeting;
    if (Array.isArray(deployNoTargeting) && deployNoTargeting.length > 0) {
      body.setAttribute('data-tutorial-overlay-deploy-no-targeting', JSON.stringify(deployNoTargeting));
    } else {
      body.removeAttribute('data-tutorial-overlay-deploy-no-targeting');
      body.removeAttribute('data-tutorial-overlay-deploy-hide-zone-front');
    }
    const deployHideZoneFront = overlayStepConfig.deployOrdersHideZoneFront;
    if (Array.isArray(deployHideZoneFront) && deployHideZoneFront.length > 0) {
      body.setAttribute('data-tutorial-overlay-deploy-hide-zone-front', JSON.stringify(deployHideZoneFront));
    } else {
      body.removeAttribute('data-tutorial-overlay-deploy-hide-zone-front');
    }
    return () => {
      OVERLAY_HIDE_ATTRS.forEach(attr => body.removeAttribute(attr));
      body.removeAttribute('data-tutorial-overlay-targeting');
      body.removeAttribute('data-tutorial-overlay-strategem-restrict');
      body.removeAttribute('data-tutorial-overlay-deploy-no-targeting');
      body.removeAttribute('data-tutorial-overlay-deploy-hide-zone-front');
      body.removeAttribute('data-tutorial-overlay-weapon-attach-restrict');
      body.removeAttribute('data-tutorial-overlay-attack-require-weapon-attached');
      body.removeAttribute('data-tutorial-overlay-attack-restrict');
      body.removeAttribute('data-tutorial-overlay-disable-cancel-targeting');
      for (let i = body.attributes.length - 1; i >= 0; i--) {
        const name = body.attributes[i].name;
        if (name.startsWith('data-tutorial-hide-opponent-unit-') || name.startsWith('data-tutorial-hide-friendly-unit-')) body.removeAttribute(name);
      }
    };
  }, [overlayStepConfig, missionId, currentTutorial?.stepId, currentTutorial?.title, suppressTutorialDialog]);

  // Sync overlay to backend: strategem restrict + endTurnActive→canEndTurn (so server allows attacks/end turn when overlay permits)
  useEffect(() => {
    if (!gameState?.scriptState?.tutorialOverlay || !gameId) return;
    const strategemAllow = overlayStepConfig?.strategemTargetAllowOnly && typeof overlayStepConfig.strategemTargetAllowOnly === 'object'
      ? overlayStepConfig.strategemTargetAllowOnly
      : {};
    const current = gameState.scriptState.currentAllowedActions || {};
    const allowedStrategemOrdersFromTarget = Object.keys(strategemAllow).map(k => parseInt(k, 10)).filter(n => !Number.isNaN(n));
    const clickableInHand = Array.isArray(overlayStepConfig?.clickableInHand) ? overlayStepConfig.clickableInHand : null;
    const allowedStrategemOrders = (clickableInHand && clickableInHand.length > 0)
      ? clickableInHand.map(id => parseInt(id, 10)).filter(n => !Number.isNaN(n))
      : (allowedStrategemOrdersFromTarget.length > 0 ? allowedStrategemOrdersFromTarget : current.allowedStrategemOrders);
    const allowed = {
      ...current,
      allowedStrategemTargetOrderIds: strategemAllow,
      allowedStrategemOrders
    };
    // When no overlay step is active (e.g. after clicking Continue), allow end turn so the button works
    if (!overlayStepConfig) {
      if (missionId === 'chapter_1_mission_1' && currentTutorial?.stepId === 'attack_the_general') {
        allowed.canEndTurn = opportunist302AllowsPassTurnDuringAttackGeneralStep(gameState, userId);
      } else {
        allowed.canEndTurn = true;
      }
    } else if (overlayStepConfig.freePlay === true) {
      // Free play: no deploy/attack restrictions, allow end turn
      allowed.freePlay = true;
      allowed.canEndTurn = true;
      delete allowed.allowedDeployOrders;
      delete allowed.allowedDeployZones;
      delete allowed.allowedAttackers;
      delete allowed.allowedAttackTargets;
    } else if (missionId === 'chapter_1_mission_3' && currentTutorial?.stepId === 'samuel_johnson') {
      // Allow end turn only after Samuel (386) has been deployed
      const playerState = gameState?.state?.[userId];
      const front = playerState?.playedOrders?.front || [];
      const flank = playerState?.playedOrders?.flank || [];
      const samuelDeployed = [...front, ...flank].some(u => u && (Number(u.id) === 386 || Number(u.orderId) === 386));
      allowed.canEndTurn = !!samuelDeployed;
    } else if (missionId === 'chapter_1_mission_1' && currentTutorial?.stepId === 'attack_the_general') {
      allowed.canEndTurn = opportunist302AllowsPassTurnDuringAttackGeneralStep(gameState, userId);
    } else if (typeof overlayStepConfig.endTurnActive === 'boolean') {
      allowed.canEndTurn = overlayStepConfig.endTurnActive;
    }
    if (overlayStepConfig?.allowedAttackers != null && Array.isArray(overlayStepConfig.allowedAttackers)) {
      allowed.allowedAttackers = overlayStepConfig.allowedAttackers;
    }
    if (overlayStepConfig?.allowedAttackTargets != null && Array.isArray(overlayStepConfig.allowedAttackTargets)) {
      allowed.allowedAttackTargets = overlayStepConfig.allowedAttackTargets;
    } else if (overlayStepConfig) {
      // Clear attack target restriction so player can attack general (e.g. Final blow after Kill threats)
      allowed.allowedAttackTargets = undefined;
    }
    if (overlayStepConfig) {
      const weaponIds = overlayStepConfig.allowedWeaponAttachTargetOrderIds;
      allowed.allowedWeaponAttachTargetOrderIds = Array.isArray(weaponIds) && weaponIds.length > 0 ? weaponIds : undefined;
      const requireWeapon = overlayStepConfig.allowedAttackersRequireWeaponAttached;
      allowed.allowedAttackersRequireWeaponAttached = Array.isArray(requireWeapon) && requireWeapon.length > 0 ? requireWeapon : undefined;
      // Mission 1: explicit allowedDeployZones / allowedDeployOrders per step (server validates playOrder).
      // Empty arrays block all deploys; omitted keys clear stale restrictions from prior steps.
      if (missionId === 'chapter_1_mission_1') {
        if (Object.prototype.hasOwnProperty.call(overlayStepConfig, 'allowedDeployZones')) {
          const z = overlayStepConfig.allowedDeployZones;
          allowed.allowedDeployZones = Array.isArray(z)
            ? z.filter((x) => x === 'front' || x === 'flank')
            : [];
        } else {
          delete allowed.allowedDeployZones;
        }
        if (Object.prototype.hasOwnProperty.call(overlayStepConfig, 'allowedDeployOrders')) {
          const o = overlayStepConfig.allowedDeployOrders;
          allowed.allowedDeployOrders = Array.isArray(o)
            ? o.map(id => parseInt(id, 10)).filter(n => !Number.isNaN(n))
            : [];
        } else {
          delete allowed.allowedDeployOrders;
        }
      } else {
        if (Array.isArray(overlayStepConfig.allowedDeployOrders) && overlayStepConfig.allowedDeployOrders.length > 0) {
          allowed.allowedDeployOrders = overlayStepConfig.allowedDeployOrders.map(id => parseInt(id, 10)).filter(n => !Number.isNaN(n));
        }
        if (Array.isArray(overlayStepConfig.allowedDeployZones) && overlayStepConfig.allowedDeployZones.length > 0) {
          allowed.allowedDeployZones = overlayStepConfig.allowedDeployZones;
        }
      }
    }
    if (!gameState.scriptState.currentAllowedActions) gameState.scriptState.currentAllowedActions = {};
    gameState.scriptState.currentAllowedActions.allowedStrategemTargetOrderIds = strategemAllow;
    gameState.scriptState.currentAllowedActions.allowedStrategemOrders = allowed.allowedStrategemOrders;
    if (allowed.canEndTurn !== undefined) gameState.scriptState.currentAllowedActions.canEndTurn = allowed.canEndTurn;
    if (allowed.freePlay !== undefined) gameState.scriptState.currentAllowedActions.freePlay = allowed.freePlay;
    if (allowed.freePlay === true) {
      delete gameState.scriptState.currentAllowedActions.allowedDeployOrders;
      delete gameState.scriptState.currentAllowedActions.allowedDeployZones;
      delete gameState.scriptState.currentAllowedActions.allowedAttackers;
      delete gameState.scriptState.currentAllowedActions.allowedAttackTargets;
    } else {
      if (allowed.allowedAttackers !== undefined) gameState.scriptState.currentAllowedActions.allowedAttackers = allowed.allowedAttackers;
      if (allowed.allowedAttackTargets !== undefined) gameState.scriptState.currentAllowedActions.allowedAttackTargets = allowed.allowedAttackTargets;
      else delete gameState.scriptState.currentAllowedActions.allowedAttackTargets;
    }
    if (allowed.allowedWeaponAttachTargetOrderIds !== undefined) gameState.scriptState.currentAllowedActions.allowedWeaponAttachTargetOrderIds = allowed.allowedWeaponAttachTargetOrderIds;
    if (allowed.allowedAttackersRequireWeaponAttached !== undefined) gameState.scriptState.currentAllowedActions.allowedAttackersRequireWeaponAttached = allowed.allowedAttackersRequireWeaponAttached;
    if (allowed.freePlay !== true) {
      if (missionId === 'chapter_1_mission_1') {
        if (allowed.allowedDeployOrders !== undefined) {
          gameState.scriptState.currentAllowedActions.allowedDeployOrders = allowed.allowedDeployOrders;
        } else {
          delete gameState.scriptState.currentAllowedActions.allowedDeployOrders;
        }
        if (allowed.allowedDeployZones !== undefined) {
          gameState.scriptState.currentAllowedActions.allowedDeployZones = allowed.allowedDeployZones;
        } else {
          delete gameState.scriptState.currentAllowedActions.allowedDeployZones;
        }
        // Opportunist-only attack step: clear "Finish them" weapon-attach locks if still on scriptState (sync omits deletes when JSON has no key)
        if (currentTutorial?.stepId === 'attack_the_general') {
          delete gameState.scriptState.currentAllowedActions.allowedAttackersRequireWeaponAttached;
          delete gameState.scriptState.currentAllowedActions.allowedWeaponAttachTargetOrderIds;
          delete allowed.allowedAttackersRequireWeaponAttached;
          delete allowed.allowedWeaponAttachTargetOrderIds;
        }
      } else {
        if (allowed.allowedDeployOrders !== undefined) gameState.scriptState.currentAllowedActions.allowedDeployOrders = allowed.allowedDeployOrders;
        if (allowed.allowedDeployZones !== undefined) gameState.scriptState.currentAllowedActions.allowedDeployZones = allowed.allowedDeployZones;
      }
    }
    window.dispatchEvent(new CustomEvent('campaign-sync-allowed-actions', {
      detail: { gameId, currentAllowedActions: allowed }
    }));
  }, [gameId, userId, missionId, gameState?.scriptState?.tutorialOverlay, gameState?.state, gameState?.turnNumber, currentTutorial?.stepId, currentTutorial?.title, overlayStepConfig?.strategemTargetAllowOnly, overlayStepConfig?.endTurnActive, overlayStepConfig?.freePlay, overlayStepConfig?.allowedAttackers, overlayStepConfig?.allowedAttackTargets, overlayStepConfig?.allowedWeaponAttachTargetOrderIds, overlayStepConfig?.allowedAttackersRequireWeaponAttached, overlayStepConfig?.clickableInHand, overlayStepConfig?.allowedDeployOrders, overlayStepConfig?.allowedDeployZones]);

  // Overlay auto-advance: end_turn (click), player_damage, opponent_damage, opponent_lost_unit_X, player_lost_unit_X
  useEffect(() => {
    if (!overlayStepConfig || !overlayStepConfig.endDismissal) return;
    const dismissal = overlayStepConfig.endDismissal;
    let hasAdvanced = false;
    const advance = () => {
      if (hasAdvanced) return;
      hasAdvanced = true;
      continueTutorial();
    };
    if (dismissal === 'end_turn') {
      const handler = (e) => {
        if (e.target.closest('.end-turn-button')) advance();
      };
      document.addEventListener('click', handler, true);
      return () => document.removeEventListener('click', handler, true);
    }
    if (dismissal === 'player_damage' || dismissal === 'opponent_damage') {
      const prevHealthRef = { player: null, opponent: null };
      const iv = setInterval(() => {
        if (hasAdvanced) return;
        const state = gameState?.state;
        if (!state || !userId) return;
        const opponentId = Object.keys(state).find(id => id !== userId);
        const playerHealth = state[userId]?.health;
        const opponentHealth = opponentId ? state[opponentId]?.health : null;
        if (prevHealthRef.player !== null && playerHealth !== prevHealthRef.player && dismissal === 'player_damage' && playerHealth < prevHealthRef.player) advance();
        if (prevHealthRef.opponent !== null && opponentHealth !== prevHealthRef.opponent && dismissal === 'opponent_damage' && opponentHealth < prevHealthRef.opponent) advance();
        prevHealthRef.player = playerHealth;
        prevHealthRef.opponent = opponentHealth;
      }, 300);
      return () => clearInterval(iv);
    }
    const matchUnitLost = /^(opponent_lost_unit_|player_lost_unit_)(\d+)$/.exec(dismissal);
    if (matchUnitLost) {
      const side = matchUnitLost[1].startsWith('opponent') ? 'opponent' : 'player';
      const orderId = parseInt(matchUnitLost[2], 10);
      let hadUnitRef = false;
      const iv = setInterval(() => {
        if (hasAdvanced) return;
        const state = gameState?.state;
        if (!state || !userId) return;
        const opponentId = Object.keys(state).find(id => id !== userId);
        const checkId = side === 'player' ? userId : opponentId;
        const played = state[checkId]?.playedOrders;
        const allUnits = played ? (played.front || []).concat(played.flank || []).filter(Boolean) : [];
        const hasUnit = allUnits.some(u => u.id === orderId);
        if (hasUnit) hadUnitRef = true;
        if (hadUnitRef && !hasUnit) advance();
      }, 400);
      return () => clearInterval(iv);
    }
    // player_deployed_<orderId>_<zone>: advance when that unit is in the player's zone (e.g. "player_deployed_294_front")
    const matchDeployed = /^player_deployed_(\d+)_(front|flank)$/.exec(dismissal);
    if (matchDeployed) {
      const orderId = parseInt(matchDeployed[1], 10);
      const zone = matchDeployed[2];
      const iv = setInterval(() => {
        if (hasAdvanced) return;
        const state = gameState?.state;
        if (!state || !userId) return;
        const played = state[userId]?.playedOrders?.[zone] || [];
        const hasUnit = played.some(u => u && u.id === orderId);
        if (hasUnit) advance();
      }, 400);
      return () => clearInterval(iv);
    }
  }, [overlayStepConfig, continueTutorial, gameState?.state, userId]);

  // Auto-advance deploy tutorial when Blackguard Operative (order 294) is deployed
  useEffect(() => {
    const isDeployTutorial = currentTutorial?.stepId === 'deploy';
    if (!isDeployTutorial) return;
    // When overlay config drives Deploy (e.g. player_deployed_294_front), overlay effect handles advance
    if (overlayStepConfig?.endDismissal && /^player_deployed_\d+_(front|flank)$/.test(overlayStepConfig.endDismissal)) return;

    let hasAdvanced = false; // Prevent multiple advances
    
    // Poll for Blackguard Operative deployment by checking the DOM
    // The Blackguard Operative should appear in the front zone when deployed
    const checkForBlackguardOperative = () => {
      if (hasAdvanced) return true; // Already advanced
      
      const frontZone = document.querySelector('.battle-zone.front');
      if (!frontZone) return false;
      
      // Check if any unit card in the front zone contains "Blackguard Operative" in its text
      // Units are rendered as cards with their name visible
      const zoneContent = frontZone.textContent || '';
      const hasBlackguardOperative = zoneContent.includes('Blackguard Operative');
      
      // Also check for any visible unit elements (more reliable)
      const unitCards = frontZone.querySelectorAll('[class*="card"], [class*="unit"], [class*="order"]');
      for (const card of unitCards) {
        const cardText = card.textContent || '';
        if (cardText.includes('Blackguard Operative')) {
          return true;
        }
      }
      
      return hasBlackguardOperative;
    };
    
    // Poll every 500ms when deploy tutorial is active
    const pollInterval = setInterval(() => {
      if (checkForBlackguardOperative() && !hasAdvanced) {
        hasAdvanced = true;
        console.log('✅ Blackguard Operative deployed - advancing to next tutorial');
        clearInterval(pollInterval);
        continueTutorial();
      }
    }, 500);
    
    return () => {
      clearInterval(pollInterval);
    };
  }, [currentTutorial, continueTutorial]);

  // Auto-advance Stratagems tutorial when Shield unit (320) is requested and targets Opportunist (302)
  useEffect(() => {
    const isStratagemsTutorial = currentTutorial?.stepId === 'stratagems';
    if (!isStratagemsTutorial) return;
    
    let hasAdvanced = false; // Prevent multiple advances
    
    // Poll to check if Shield unit has been requested and Opportunist has shield
    const checkForShieldOnOpportunist = () => {
      if (hasAdvanced) return true; // Already advanced
      
      // Check DOM for Opportunist with shield - look for shield visual indicators
      const frontZone = document.querySelector('.battle-zone.front');
      if (!frontZone) {
        console.log('🔍 Stratagems check: Front zone not found');
        return false;
      }
      
      // Check if Opportunist is in the front zone
      const zoneContent = frontZone.textContent || '';
      const hasOpportunist = zoneContent.includes('Opportunist');
      
      if (!hasOpportunist) {
        console.log('🔍 Stratagems check: Opportunist not found in front zone');
        return false;
      }
      
      // Simple heuristic: If Opportunist is deployed AND Shield unit is no longer in hand,
      // then Shield unit was likely played. Advance tutorial.
      const orderCarousel = document.querySelector('.order-carousel');
      const carouselText = orderCarousel?.textContent || '';
      
      // Check if Shield unit is still in hand
      // Look for "Shield unit" text or order ID 320
      const shieldInHand = carouselText.includes('Shield unit') || 
                          carouselText.includes('Shield') ||
                          orderCarousel?.querySelector('[data-order-id="320"]') ||
                          orderCarousel?.querySelector('[data-order-name*="Shield"]');
      
      // If Opportunist is deployed and Shield unit is NOT in hand, it was played
      const shieldWasPlayed = !shieldInHand && hasOpportunist;
      
      // Also check for visual shield indicators as backup
      const zoneHasShieldIndicator = zoneContent.includes('Shield') ||
                                     frontZone.querySelector('[style*="rgba(135, 206, 235"]') ||
                                     frontZone.querySelector('[class*="shield"]');
      
      const result = shieldWasPlayed || (hasOpportunist && zoneHasShieldIndicator);
      
      if (result) {
        console.log('✅ Stratagems check: Shield unit was played on Opportunist', {
          hasOpportunist,
          shieldInHand,
          shieldWasPlayed,
          zoneHasShieldIndicator
        });
      }
      
      return result;
    };
    
    // Poll every 500ms when Stratagems tutorial is active
    const pollInterval = setInterval(() => {
      if (checkForShieldOnOpportunist() && !hasAdvanced) {
        hasAdvanced = true;
        console.log('✅ Shield unit requested and Opportunist has shield - advancing to next tutorial');
        clearInterval(pollInterval);
        continueTutorial();
      }
    }, 500);
    
    return () => {
      clearInterval(pollInterval);
    };
  }, [currentTutorial, continueTutorial]);

  // Auto-advance End turn tutorial when player clicks end turn button
  useEffect(() => {
    const isEndTurnTutorial = (currentTutorial?.stepId === 'end_turn_1' || currentTutorial?.stepId === 'end_turn_2');
    if (!isEndTurnTutorial) return;
    
    let hasAdvanced = false; // Prevent multiple advances
    
    // Listen for end turn button click
    const handleEndTurnClick = (event) => {
      // Check if the clicked element is the end turn button or inside it
      const endTurnButton = event.target.closest('.end-turn-button');
      if (endTurnButton && !hasAdvanced) {
        hasAdvanced = true;
        console.log('✅ End turn button clicked - advancing to next tutorial');
        // Small delay to let the turn end process
        setTimeout(() => {
          continueTutorial();
        }, 500);
      }
    };
    
    // Also check if turn has changed (player's turn is now false)
    const checkTurnChanged = () => {
      if (hasAdvanced) return;
      
      // Check if end turn button is disabled (means it's not player's turn anymore)
      const endTurnButton = document.querySelector('.end-turn-button');
      if (endTurnButton) {
        const isDisabled = endTurnButton.disabled || 
                          endTurnButton.style.pointerEvents === 'none' ||
                          endTurnButton.style.opacity === '0.5';
        
        if (isDisabled && !hasAdvanced) {
          hasAdvanced = true;
          console.log('✅ Turn ended (button disabled) - advancing to next tutorial');
          continueTutorial();
        }
      }
    };
    
    // Add click listener to document (event delegation)
    document.addEventListener('click', handleEndTurnClick, true);
    
    // Also poll to check if turn changed
    const pollInterval = setInterval(checkTurnChanged, 500);
    
    return () => {
      document.removeEventListener('click', handleEndTurnClick, true);
      clearInterval(pollInterval);
    };
  }, [currentTutorial, continueTutorial]);

  // Auto-advance "Deploy Effects" tutorial when player clicks end turn button
  useEffect(() => {
    if (currentTutorial?.stepId !== 'deploy_effects') return;

    let hasAdvanced = false;

    const handleEndTurnClick = (event) => {
      const endTurnButton = event.target.closest('.end-turn-button');
      if (endTurnButton && !hasAdvanced) {
        hasAdvanced = true;
        console.log('✅ Deploy Effects: End turn clicked - advancing to next tutorial');
        setTimeout(() => {
          continueTutorial();
        }, 500);
      }
    };

    const checkTurnChanged = () => {
      if (hasAdvanced) return;
      const endTurnButton = document.querySelector('.end-turn-button');
      if (endTurnButton) {
        const isDisabled = endTurnButton.disabled ||
                          endTurnButton.style.pointerEvents === 'none' ||
                          endTurnButton.style.opacity === '0.5';
        if (isDisabled) {
          hasAdvanced = true;
          console.log('✅ Deploy Effects: Turn ended (button disabled) - advancing to next tutorial');
          continueTutorial();
        }
      }
    };

    document.addEventListener('click', handleEndTurnClick, true);
    const pollInterval = setInterval(checkTurnChanged, 500);

    return () => {
      document.removeEventListener('click', handleEndTurnClick, true);
      clearInterval(pollInterval);
    };
  }, [currentTutorial, continueTutorial]);

  // Auto-advance "Your Drones" tutorial when player clicks end turn button
  useEffect(() => {
    if (currentTutorial?.stepId !== 'your_drones') return;

    let hasAdvanced = false;

    const handleEndTurnClick = (event) => {
      const endTurnButton = event.target.closest('.end-turn-button');
      if (endTurnButton && !hasAdvanced) {
        hasAdvanced = true;
        console.log('✅ Your Drones: End turn clicked - advancing to next tutorial');
        setTimeout(() => {
          continueTutorial();
        }, 500);
      }
    };

    const checkTurnChanged = () => {
      if (hasAdvanced) return;
      const endTurnButton = document.querySelector('.end-turn-button');
      if (endTurnButton) {
        const isDisabled = endTurnButton.disabled ||
                          endTurnButton.style.pointerEvents === 'none' ||
                          endTurnButton.style.opacity === '0.5';
        if (isDisabled) {
          hasAdvanced = true;
          console.log('✅ Your Drones: Turn ended (button disabled) - advancing to next tutorial');
          continueTutorial();
        }
      }
    };

    document.addEventListener('click', handleEndTurnClick, true);
    const pollInterval = setInterval(checkTurnChanged, 500);

    return () => {
      document.removeEventListener('click', handleEndTurnClick, true);
      clearInterval(pollInterval);
    };
  }, [currentTutorial, continueTutorial]);

  // Auto-advance "Use Your Drone" tutorial when drone targets Dark Circuit Engineer (47)
  useEffect(() => {
    if (currentTutorial?.stepId !== 'use_your_drone') return;

    let hasAdvanced = false;
    const iv = setInterval(() => {
      if (hasAdvanced) return;
      const actionHistory = document.querySelector('.action-history, [class*="action-history"], [class*="ActionHistory"]');
      if (actionHistory) {
        const text = actionHistory.textContent || '';
        if (text.includes('Dark Circuit Engineer') || text.includes('Aerial Strike')) {
          hasAdvanced = true;
          console.log('✅ Use Your Drone: drone targeted Dark Circuit Engineer - advancing');
          continueTutorial();
          return;
        }
      }
      const opponentFront = document.querySelector('.battle-zone.opponent-front');
      if (opponentFront) {
        const text = opponentFront.textContent || '';
        if (!text.includes('Dark Circuit')) {
          hasAdvanced = true;
          console.log('✅ Use Your Drone: Dark Circuit Engineer no longer on field - advancing');
          continueTutorial();
        }
      }
    }, 300);

    return () => clearInterval(iv);
  }, [currentTutorial, continueTutorial]);

  // Auto-advance "Samuel Johnson" tutorial when Samuel Johnson (386) is deployed to front
  useEffect(() => {
    if (currentTutorial?.stepId !== 'samuel_johnson') return;

    let hasAdvanced = false;
    const iv = setInterval(() => {
      if (hasAdvanced) return;
      const frontZone = document.querySelector('.battle-zone.front');
      if (frontZone && frontZone.textContent?.includes('Samuel Johnson')) {
        hasAdvanced = true;
        console.log('✅ Samuel Johnson: deployed to front - advancing');
        continueTutorial();
      }
    }, 300);

    return () => clearInterval(iv);
  }, [currentTutorial, continueTutorial]);

  // Special Units: clear when End Turn clicked so backend's "Out of reach" (after bot deploys Panicked Overseer) can show
  useEffect(() => {
    if (currentTutorial?.stepId !== 'special_units') return;
    let done = false;
    const advance = () => { if (done) return; done = true; clearTutorials(); };
    const onEndTurn = (e) => { if (e.target.closest('.end-turn-button')) advance(); };
    const poll = () => { const btn = document.querySelector('.end-turn-button'); if (btn && (btn.disabled || btn.style.pointerEvents === 'none' || btn.style.opacity === '0.5')) advance(); };
    document.addEventListener('click', onEndTurn, true);
    const iv = setInterval(poll, 500);
    return () => { document.removeEventListener('click', onEndTurn, true); clearInterval(iv); };
  }, [currentTutorial, clearTutorials]);

  // Auto-advance "Preparing" tutorial when player clicks end turn button
  useEffect(() => {
    const isPreparingTutorial = currentTutorial?.stepId === 'preparing';
    if (!isPreparingTutorial) return;
    
    let hasAdvanced = false; // Prevent multiple advances
    
    // Listen for end turn button click
    const handleEndTurnClick = (event) => {
      // Check if the clicked element is the end turn button or inside it
      const endTurnButton = event.target.closest('.end-turn-button');
      if (endTurnButton && !hasAdvanced) {
        hasAdvanced = true;
        console.log('✅ Preparing tutorial: End turn button clicked - advancing to next tutorial');
        // Small delay to let the turn end process
        setTimeout(() => {
          continueTutorial();
        }, 500);
      }
    };
    
    // Also check if turn has changed (player's turn is now false)
    const checkTurnChanged = () => {
      if (hasAdvanced) return;
      
      // Check if end turn button is disabled (means it's not player's turn anymore)
      const endTurnButton = document.querySelector('.end-turn-button');
      if (endTurnButton) {
        const isDisabled = endTurnButton.disabled || 
                          endTurnButton.style.pointerEvents === 'none' ||
                          endTurnButton.style.opacity === '0.5';
        
        if (isDisabled && !hasAdvanced) {
          hasAdvanced = true;
          console.log('✅ Preparing tutorial: Turn ended (button disabled) - advancing to next tutorial');
          continueTutorial();
        }
      }
    };
    
    // Add click listener to document (event delegation)
    document.addEventListener('click', handleEndTurnClick, true);
    
    // Also poll to check if turn changed
    const pollInterval = setInterval(checkTurnChanged, 500);
    
    return () => {
      document.removeEventListener('click', handleEndTurnClick, true);
      clearInterval(pollInterval);
    };
  }, [currentTutorial, continueTutorial]);

  // Auto-advance "Overwhelming force" tutorial when player clicks end turn button
  useEffect(() => {
    const isOverwhelmingTutorial = currentTutorial?.stepId === 'overwhelming_force';
    if (!isOverwhelmingTutorial) return;
    
    let hasAdvanced = false; // Prevent multiple advances
    
    // Listen for end turn button click
    const handleEndTurnClick = (event) => {
      const endTurnButton = event.target.closest('.end-turn-button');
      if (endTurnButton && !hasAdvanced) {
        hasAdvanced = true;
        console.log('✅ Overwhelming force tutorial: End turn button clicked - advancing to next tutorial');
        setTimeout(() => {
          continueTutorial();
        }, 500);
      }
    };
    
    // Also check if turn has changed (player's turn is now false)
    const checkTurnChanged = () => {
      if (hasAdvanced) return;
      
      const endTurnButton = document.querySelector('.end-turn-button');
      if (endTurnButton) {
        const isDisabled = endTurnButton.disabled || 
                          endTurnButton.style.pointerEvents === 'none' ||
                          endTurnButton.style.opacity === '0.5';
        
        if (isDisabled && !hasAdvanced) {
          hasAdvanced = true;
          console.log('✅ Overwhelming force tutorial: Turn ended (button disabled) - advancing to next tutorial');
          continueTutorial();
        }
      }
    };
    
    document.addEventListener('click', handleEndTurnClick, true);
    const pollInterval = setInterval(checkTurnChanged, 500);
    
    return () => {
      document.removeEventListener('click', handleEndTurnClick, true);
      clearInterval(pollInterval);
    };
  }, [currentTutorial, continueTutorial]);

  // Helper function to disable campaign script (defined early so it can be used in useEffects)
  const disableCampaignScript = useCallback(() => {
    if (!gameId) {
      console.warn('⚠️ Campaign: Cannot disable script - no gameId');
      return Promise.resolve();
    }
    
    // Remove all blocking overlays
    const allBlockOverlays = [
      'front-block-overlay',
      'flank-block-overlay',
      'end-turn-block-overlay',
      'forsaken-sentinel-block-overlay',
      'counter-strike-player-front-block-overlay',
      'counter-strike-opponent-front-block-overlay',
      'counter-strike-keep-button-block-overlay',
      'isabela-block-overlay',
      'tranquilizer-block-overlay',
      'missile-barrage-block-overlay'
    ];

    allBlockOverlays.forEach(id => {
      const overlay = document.getElementById(id);
      if (overlay) {
        overlay.style.display = 'none';
        overlay.remove();
      }
    });
    
    // Send WebSocket message to disable campaign script
    const wsUrl = WS_URL;
    const token = localStorage.getItem('token');
    
    return new Promise((resolve, reject) => {
      const ws = new WebSocket(wsUrl);
      let authSent = false;
      let disableSent = false;
      
      ws.onopen = () => {
        console.log(`📜 Campaign: WebSocket opened, authenticating...`);
        // Authenticate first with API key
        ws.send(JSON.stringify(getWebSocketAuthMessage(token)));
        authSent = true;
        
        // Wait for auth confirmation, then send disable message
        setTimeout(() => {
          if (!disableSent) {
            ws.send(JSON.stringify({
              type: 'disableCampaignScript',
              gameId: gameId
            }));
            disableSent = true;
            console.log(`📜 Campaign: Sent disableCampaignScript message for game ${gameId}`);
            
            // Wait a bit for message to be processed, then close
            setTimeout(() => {
              ws.close();
              resolve();
            }, 200);
          }
        }, 150);
      };
      
      ws.onerror = (error) => {
        console.error(`❌ Campaign: WebSocket error while disabling script:`, error);
        reject(error);
      };
      
      ws.onclose = () => {
        if (disableSent) {
          console.log(`📜 Campaign: WebSocket closed after disabling script`);
          resolve();
        } else {
          console.warn(`⚠️ Campaign: WebSocket closed before disable message was sent`);
        }
      };
      
      // Timeout after 5 seconds
      setTimeout(() => {
        if (!disableSent) {
          console.error(`❌ Campaign: Timeout while disabling script`);
          ws.close();
          reject(new Error('Timeout'));
        }
      }, 5000);
    });
  }, [gameId]);

  // Auto-advance "Defeat your opponent" tutorial when player clicks end turn button
  useEffect(() => {
    const isDefeatTutorial = currentTutorial?.stepId === 'defeat_your_opponent';
    if (!isDefeatTutorial) return;
    
    let hasAdvanced = false; // Prevent multiple advances
    
    // Listen for end turn button click
    const handleEndTurnClick = (event) => {
      const endTurnButton = event.target.closest('.end-turn-button');
      if (endTurnButton && !hasAdvanced) {
        hasAdvanced = true;
        console.log('✅ Defeat your opponent tutorial: End turn button clicked - closing tutorial and disabling script');
        // Disable campaign script before closing tutorial
        disableCampaignScript().catch(err => {
          console.error(`❌ Campaign: Failed to disable script:`, err);
        });
        continueTutorial();
      }
    };
    
    // Add click listener to document (event delegation)
    document.addEventListener('click', handleEndTurnClick, true);
    
    return () => {
      document.removeEventListener('click', handleEndTurnClick, true);
    };
  }, [currentTutorial, continueTutorial, disableCampaignScript]);

  // ========== MISSION 4: Zone blocking ==========
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4') return;
    const t = currentTutorial?.stepId || currentTutorial?.title;
    // Skip zone blocking during mulligan phase — overlays would cover the mulligan dialog
    const skipZoneBlocking = ['Mulligan', "Ma'at Arrives", "Ma'at's Strength", 'Devastating Impact'].includes(t);
    if (skipZoneBlocking) {
      ['t4-front-block-overlay', 't4-flank-block-overlay'].forEach(id => {
        const el = document.getElementById(id);
        if (el) { el.style.display = 'none'; el.remove(); }
      });
      return;
    }
    // Determine which zones to block based on tutorial step
    const blockFlank = ['Advanced Tactics', 'Hold the Line', 'Shield Mastery', 'Clearing the Field'].includes(t);
    const blockFront = false;
    const blockBoth = t && !blockFlank && !["End the threat", "Mentora", "Harral's Betrayal"].includes(t) && tutorial4AllowedOrders !== null && tutorial4AllowedOrders.length === 0;
    // If nothing to block (free play or attack-only tutorials), clean up
    if (!blockFlank && !blockFront && !blockBoth) {
      ['t4-front-block-overlay', 't4-flank-block-overlay'].forEach(id => {
        const el = document.getElementById(id);
        if (el) { el.style.display = 'none'; el.remove(); }
      });
      return;
    }
    let frontOvr = document.getElementById('t4-front-block-overlay');
    let flankOvr = document.getElementById('t4-flank-block-overlay');
    if (!frontOvr) {
      frontOvr = document.createElement('div'); frontOvr.id = 't4-front-block-overlay';
      frontOvr.style.cssText = 'position:fixed;z-index:99999;pointer-events:auto;background:transparent;';
      document.body.appendChild(frontOvr);
    }
    if (!flankOvr) {
      flankOvr = document.createElement('div'); flankOvr.id = 't4-flank-block-overlay';
      flankOvr.style.cssText = 'position:fixed;z-index:99999;pointer-events:auto;background:transparent;';
      document.body.appendChild(flankOvr);
    }
    const update = () => {
      const fz = document.querySelector('.battle-zone.front');
      const kz = document.querySelector('.battle-zone.flank');
      if ((blockFlank || blockBoth) && kz) {
        const r = kz.getBoundingClientRect();
        flankOvr.style.display = 'block'; flankOvr.style.left = r.left + 'px'; flankOvr.style.top = r.top + 'px';
        flankOvr.style.width = r.width + 'px'; flankOvr.style.height = r.height + 'px';
      } else { flankOvr.style.display = 'none'; }
      if ((blockFront || blockBoth) && fz) {
        const r = fz.getBoundingClientRect();
        frontOvr.style.display = 'block'; frontOvr.style.left = r.left + 'px'; frontOvr.style.top = r.top + 'px';
        frontOvr.style.width = r.width + 'px'; frontOvr.style.height = r.height + 'px';
      } else { frontOvr.style.display = 'none'; }
    };
    update();
    const iv = setInterval(update, 100);
    return () => { clearInterval(iv); frontOvr.style.display = 'none'; flankOvr.style.display = 'none'; };
  }, [missionId, currentTutorial?.title, tutorial4AllowedOrders]);

  // ========== MISSION 4: End turn blocking ==========
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4') return;
    const t = currentTutorial?.stepId || currentTutorial?.title;
    // Skip during mulligan — end turn button doesn't exist yet and overlay would cover mulligan dialog
    const skipEndTurnBlock = ['Mulligan', "Ma'at Arrives", "Ma'at's Strength", 'Devastating Impact'].includes(t);
    const canEnd = skipEndTurnBlock || ['Hold the Line', 'Patience', 'Well Done',
      'Reinforcements', "Harral's Betrayal", "End the threat"].includes(t) || !t;
    if (canEnd) {
      const el = document.getElementById('t4-end-turn-block');
      if (el) { el.style.display = 'none'; el.remove(); }
      // Also force-enable the button (BattleUI may set disabled after mulligan)
      const enableBtn = () => {
        const btn = document.querySelector('.end-turn-button');
        if (btn && btn.disabled) btn.disabled = false;
      };
      enableBtn();
      const iv = setInterval(enableBtn, 200);
      return () => clearInterval(iv);
    }
    let ovr = document.getElementById('t4-end-turn-block');
    if (!ovr) {
      ovr = document.createElement('div'); ovr.id = 't4-end-turn-block';
      ovr.style.cssText = 'position:fixed;z-index:100001;pointer-events:auto;background:transparent;';
      document.body.appendChild(ovr);
    }
    const update = () => {
      const btn = document.querySelector('.end-turn-button');
      if (btn) {
        const r = btn.getBoundingClientRect();
        ovr.style.display = 'block'; ovr.style.left = r.left + 'px'; ovr.style.top = r.top + 'px';
        ovr.style.width = r.width + 'px'; ovr.style.height = r.height + 'px';
      } else { ovr.style.display = 'none'; }
    };
    update();
    const iv = setInterval(update, 100);
    return () => { clearInterval(iv); if (ovr) ovr.style.display = 'none'; };
  }, [missionId, currentTutorial?.title]);

  // ========== MISSION 4: Body data attributes for targeting restrictions ==========
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4') return;
    const t = currentTutorial?.stepId || currentTutorial?.title;
    const body = document.body;
    body.removeAttribute('data-tutorial4-attack-restrict');
    body.removeAttribute('data-tutorial4-deploy-target-restrict');
    body.removeAttribute('data-tutorial4-strategem-target-restrict');
    body.removeAttribute('data-tutorial4-hide-targeting');

    if (!t || t === "End the threat") return;

    // Hide targeting visualization for most tutorial steps (only show during specific action steps)
    const showTargeting = ['Advanced Tactics', 'Mentora', 'Shield Mastery', 'Clearing the Field', "Harral's Betrayal"].includes(t);
    if (!showTargeting) {
      body.setAttribute('data-tutorial4-hide-targeting', 'true');
    }

    if (t === 'Mentora') {
      body.setAttribute('data-tutorial4-attack-restrict', JSON.stringify({ attackers: [288], targets: [27] }));
    } else if (t === "Harral's Betrayal") {
      body.setAttribute('data-tutorial4-attack-restrict', JSON.stringify({ attackers: [299] }));
    }
    if (t === 'Shield Mastery') {
      body.setAttribute('data-tutorial4-deploy-target-restrict', JSON.stringify({ deployOrderId: 309, targetOrderId: 288 }));
    }
    if (t === 'Advanced Tactics') {
      body.setAttribute('data-tutorial4-strategem-target-restrict', JSON.stringify({ strategemId: 314, targetOrderId: 282 }));
    } else if (t === 'Shield Mastery') {
      body.setAttribute('data-tutorial4-strategem-target-restrict', JSON.stringify({ strategemId: 317, targetOrderId: 288 }));
    }

    return () => {
      body.removeAttribute('data-tutorial4-attack-restrict');
      body.removeAttribute('data-tutorial4-deploy-target-restrict');
      body.removeAttribute('data-tutorial4-strategem-target-restrict');
      body.removeAttribute('data-tutorial4-hide-targeting');
    };
  }, [missionId, currentTutorial?.title]);

  // ========== MISSION 4: Sync currentAllowedActions to backend ==========
  // Local state is updated for UI; we also sync to server so play-turn validation can enforce restrictions.
  // CRITICAL: For "End the threat" (free play) we must always sync so player can deploy and end turn.
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4' || !gameState) return;
    const t = currentTutorial?.stepId || currentTutorial?.title;
    const map = {
      'Mulligan': { mulliganOnly: true },
      "Ma'at Arrives": { continueOnly: true },
      "Ma'at's Strength": { canEndTurn: true },
      'Devastating Impact': { continueOnly: true },
      'Advanced Tactics': { allowedDeployOrders: [282], allowedDeployZones: ['front'], allowedStrategemOrders: [314] },
      'Hold the Line': { allowedDeployOrders: [288], allowedDeployZones: ['front'], canEndTurn: true },
      'Mentora': { allowedAttackers: [288], allowedAttackTargets: [27] },
      'Shield Mastery': { allowedDeployOrders: [309, 287], allowedDeployZones: ['front'], allowedStrategemOrders: [317], allowedDeployTargetsByOrder: { 309: [288] }, allowedStrategemTargetOrderIds: { 317: 288 } },
      'Patience': { canEndTurn: true },
      'Clearing the Field': { allowedDeployOrders: [291], allowedDeployZones: ['front'], allowedStrategemOrders: [325] },
      'Well Done': { canEndTurn: true },
      'Reinforcements': { canEndTurn: true },
      "Harral's Betrayal": { allowedAttackers: [299], allowedDeployOrders: [307], allowedDeployZones: ['flank'], canEndTurn: true },
      "End the threat": { freePlay: true },
    };
    const allowed = t && map[t] ? map[t] : { freePlay: true };
    // Ensure scriptState exists so we never skip syncing freePlay (fixes Ma'at's Farewell break)
    if (!gameState.scriptState) {
      gameState.scriptState = {};
    }
    gameState.scriptState.currentAllowedActions = allowed;
    // Sync to server so backend can reject play-turn when e.g. continueOnly (Ma'at Arrives)
    if (gameId) {
      window.dispatchEvent(new CustomEvent('campaign-sync-allowed-actions', {
        detail: { gameId, currentAllowedActions: allowed }
      }));
    }
  }, [missionId, currentTutorial?.title, gameState, gameId]);

  // When BattleUI requests re-sync (e.g. after socket reconnect), send current allowed actions to server
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4' || !gameId || !gameState) return;
    const handler = (e) => {
      if (e.detail?.gameId !== gameId) return;
      const t = currentTutorial?.stepId || currentTutorial?.title;
      const map = {
        'Mulligan': { mulliganOnly: true },
        "Ma'at Arrives": { continueOnly: true },
        "Ma'at's Strength": { canEndTurn: true },
        'Devastating Impact': { continueOnly: true },
        'Advanced Tactics': { allowedDeployOrders: [282], allowedDeployZones: ['front'], allowedStrategemOrders: [314] },
        'Hold the Line': { allowedDeployOrders: [288], allowedDeployZones: ['front'], canEndTurn: true },
        'Mentora': { allowedAttackers: [288], allowedAttackTargets: [27] },
        'Shield Mastery': { allowedDeployOrders: [309, 287], allowedDeployZones: ['front'], allowedStrategemOrders: [317] },
        'Patience': { canEndTurn: true },
        'Clearing the Field': { allowedDeployOrders: [291], allowedDeployZones: ['front'], allowedStrategemOrders: [325] },
        'Well Done': { canEndTurn: true },
        'Reinforcements': { canEndTurn: true },
        "Harral's Betrayal": { allowedAttackers: [299], allowedDeployOrders: [307], allowedDeployZones: ['flank'], canEndTurn: true },
        "End the threat": { freePlay: true },
      };
      const allowed = t && map[t] ? map[t] : { freePlay: true };
      window.dispatchEvent(new CustomEvent('campaign-sync-allowed-actions', {
        detail: { gameId, currentAllowedActions: allowed }
      }));
    };
    window.addEventListener('campaign-request-sync', handler);
    return () => window.removeEventListener('campaign-request-sync', handler);
  }, [missionId, gameId, gameState, currentTutorial?.title]);

  // ========== MISSION 4: Auto-advance "Mulligan" when hasMulliganed ==========
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4' || currentTutorial?.stepId !== 'mulligan') return;
    let hasAdv = false;
    const iv = setInterval(() => {
      if (hasAdv) return;
      const uid = userId || parseInt(localStorage.getItem('userId'));
      const ps = gameState?.state?.[uid] || gameState?.state?.[String(uid)];
      if (ps?.hasMulliganed) {
        hasAdv = true; clearInterval(iv);
        console.log('✅ Tutorial 4: Mulligan complete - advancing');
        continueTutorial();
      }
    }, 300);
    return () => clearInterval(iv);
  }, [missionId, currentTutorial?.title, gameState, userId, continueTutorial]);

  // ========== MISSION 4: Mulligan card selection restriction + submit button control ==========
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4' || currentTutorial?.stepId !== 'mulligan') return;
    const selectableIds = gameState?.mulliganSelectableOrders || [386, 307];
    const restrict = () => {
      // Block non-selectable cards (only SA is blocked, SJ and AC are selectable)
      const mulliganCards = document.querySelectorAll('[data-mulligan-order-id]');
      mulliganCards.forEach(card => {
        const oid = parseInt(card.getAttribute('data-mulligan-order-id'), 10);
        if (!selectableIds.includes(oid)) {
          card.style.pointerEvents = 'none'; card.style.opacity = '0.5';
        } else {
          card.style.pointerEvents = ''; card.style.opacity = '';
        }
      });
      // Disable submit button until both required orders (386, 307) are selected
      const submitBtn = document.querySelector('.mulligan-submit-button');
      if (submitBtn) {
        const selectedCards = document.querySelectorAll('.mulligan-order-selected');
        const selectedIds = new Set();
        selectedCards.forEach(sel => {
          const wrapper = sel.closest('[data-mulligan-order-id]');
          if (wrapper) selectedIds.add(parseInt(wrapper.getAttribute('data-mulligan-order-id'), 10));
        });
        const allRequired = selectableIds.every(id => selectedIds.has(id));
        if (!allRequired) {
          submitBtn.style.pointerEvents = 'none'; submitBtn.style.opacity = '0.4';
        } else {
          submitBtn.style.pointerEvents = ''; submitBtn.style.opacity = '';
        }
      }
    };
    restrict();
    const iv = setInterval(restrict, 200);
    return () => {
      clearInterval(iv);
      document.querySelectorAll('[data-mulligan-order-id]').forEach(c => { c.style.pointerEvents = ''; c.style.opacity = ''; });
      const btn = document.querySelector('.mulligan-submit-button');
      if (btn) { btn.style.pointerEvents = ''; btn.style.opacity = ''; }
    };
  }, [missionId, currentTutorial?.title, gameState]);

  // ========== MISSION 4: Auto-advance "Advanced Tactics" when YP+Kamikaze done ==========
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4' || currentTutorial?.stepId !== 'advanced_tactics') return;
    let hasAdv = false;
    const iv = setInterval(() => {
      if (hasAdv) return;
      const carousel = document.querySelector('.order-carousel');
      const carouselText = carousel?.textContent || '';
      const ypGone = !carouselText.includes('Youthful Patriot') && !carousel?.querySelector('[data-order-id="282"]');
      const kamGone = !carouselText.includes('Kamikaze') && !carousel?.querySelector('[data-order-id="314"]');
      if (ypGone && kamGone) {
        hasAdv = true; clearInterval(iv);
        console.log('✅ Tutorial 4: YP+Kamikaze combo complete - advancing');
        setTimeout(() => continueTutorial(), 500);
      }
    }, 400);
    return () => clearInterval(iv);
  }, [missionId, currentTutorial?.title, continueTutorial]);

  // ========== MISSION 4: Auto-advance "Ma'at's Strength" on end turn ==========
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4' || currentTutorial?.stepId !== 'maats_strength') return;
    let hasAdv = false;
    const handler = (e) => {
      if (hasAdv) return;
      if (e.target.closest('.end-turn-button')) { hasAdv = true; continueTutorial(); }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [missionId, currentTutorial?.title, continueTutorial]);

  // ========== MISSION 4: Auto-advance "Hold the Line" on end turn ==========
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4' || currentTutorial?.stepId !== 'hold_the_line') return;
    let hasAdv = false;
    const handler = (e) => {
      if (hasAdv) return;
      if (e.target.closest('.end-turn-button')) { hasAdv = true; continueTutorial(); }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [missionId, currentTutorial?.title, continueTutorial]);

  // ========== MISSION 4: Auto-advance "Mentora" when SA attacks ==========
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4' || currentTutorial?.stepId !== 'mentora') return;
    let hasAdv = false;
    const iv = setInterval(() => {
      if (hasAdv) return;
      const uid = userId || parseInt(localStorage.getItem('userId'));
      const ps = gameState?.state?.[uid] || gameState?.state?.[String(uid)];
      const saUnit = (ps?.playedOrders?.front || []).find(u => u && u.id === 288);
      if (saUnit && saUnit.hasAttacked) {
        hasAdv = true; clearInterval(iv);
        console.log('✅ Tutorial 4: SA attacked - advancing');
        setTimeout(() => continueTutorial(), 800);
      }
    }, 400);
    return () => clearInterval(iv);
  }, [missionId, currentTutorial?.title, gameState, userId, continueTutorial]);

  // ========== MISSION 4: Auto-advance "Shield Mastery" when Guided Missile (317) is used ==========
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4' || currentTutorial?.stepId !== 'shield_mastery') return;
    let hasAdv = false;
    const iv = setInterval(() => {
      if (hasAdv) return;
      const carousel = document.querySelector('.order-carousel');
      const bgGone = !carousel?.querySelector('[data-order-id="287"]');
      const gmGone = !carousel?.querySelector('[data-order-id="317"]');
      if (bgGone && gmGone) {
        hasAdv = true; clearInterval(iv);
        console.log('✅ Tutorial 4: Guided Missile used - advancing');
        setTimeout(() => continueTutorial(), 800);
      }
    }, 400);
    return () => clearInterval(iv);
  }, [missionId, currentTutorial?.title, continueTutorial]);

  // ========== MISSION 4: Auto-advance "Patience" on end turn ==========
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4' || currentTutorial?.stepId !== 'patience') return;
    let hasAdv = false;
    const handler = (e) => {
      if (hasAdv) return;
      if (e.target.closest('.end-turn-button')) { hasAdv = true; continueTutorial(); }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [missionId, currentTutorial?.title, continueTutorial]);

  // ========== MISSION 4: Auto-advance "Clearing the Field" when PB+OP deployed ==========
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4' || currentTutorial?.stepId !== 'clearing_the_field') return;
    let hasAdv = false;
    const iv = setInterval(() => {
      if (hasAdv) return;
      const carousel = document.querySelector('.order-carousel');
      const pbGone = !carousel?.querySelector('[data-order-id="291"]');
      const opGone = !carousel?.querySelector('[data-order-id="325"]');
      if (pbGone && opGone) {
        hasAdv = true; clearInterval(iv);
        console.log('✅ Tutorial 4: PB+OP combo complete - advancing');
        setTimeout(() => continueTutorial(), 800);
      }
    }, 400);
    return () => clearInterval(iv);
  }, [missionId, currentTutorial?.title, continueTutorial]);

  // ========== MISSION 4: Auto-advance "Well Done" on end turn ==========
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4' || currentTutorial?.stepId !== 'well_done') return;
    let hasAdv = false;
    const handler = (e) => {
      if (hasAdv) return;
      if (e.target.closest('.end-turn-button')) { hasAdv = true; continueTutorial(); }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [missionId, currentTutorial?.title, continueTutorial]);

  // ========== MISSION 4: Auto-advance "Reinforcements" on end turn ==========
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4' || currentTutorial?.stepId !== 'reinforcements') return;
    let hasAdv = false;
    const handler = (e) => {
      if (hasAdv) return;
      if (e.target.closest('.end-turn-button')) { hasAdv = true; continueTutorial(); }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [missionId, currentTutorial?.title, continueTutorial]);

  // ========== MISSION 4: Auto-advance "Harral's Betrayal" on end turn (after attack + AC deploy) ==========
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4' || currentTutorial?.stepId !== 'harrals_betrayal') return;
    let hasAdv = false;
    const handler = (e) => {
      if (hasAdv) return;
      if (e.target.closest('.end-turn-button')) { hasAdv = true; continueTutorial(); }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [missionId, currentTutorial?.title, continueTutorial]);

  // ========== MISSION 4: Auto-advance "End the threat" on end turn — close dialog and disable script so bot uses normal AI ==========
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_4' || currentTutorial?.stepId !== 'end_the_threat') return;
    let hasAdv = false;
    const handler = (e) => {
      if (hasAdv) return;
      if (e.target.closest('.end-turn-button')) {
        hasAdv = true;
        disableCampaignScript().catch(err => console.error('Campaign: Failed to disable script:', err));
        continueTutorial();
      }
    };
    document.addEventListener('click', handler, true);
    return () => document.removeEventListener('click', handler, true);
  }, [missionId, currentTutorial?.title, continueTutorial, disableCampaignScript]);

  // ✅ CRITICAL: Continuously remove all blocking overlays when tutorial ends (currentTutorial is null)
  useEffect(() => {
    if (!currentTutorial) {
      // No tutorial active - continuously remove all blocking overlays
      const allBlockOverlays = [
        'front-block-overlay',
        'flank-block-overlay',
        'end-turn-block-overlay',
        'forsaken-sentinel-block-overlay',
        'counter-strike-player-front-block-overlay',
        'counter-strike-opponent-front-block-overlay',
      'counter-strike-keep-button-block-overlay',
      'isabela-block-overlay',
      'tranquilizer-block-overlay',
      'missile-barrage-block-overlay',
      't4-front-block-overlay',
      't4-flank-block-overlay',
      't4-end-turn-block'
    ];

      const removeAllOverlays = () => {
        allBlockOverlays.forEach(id => {
          const overlay = document.getElementById(id);
          if (overlay) {
            overlay.style.display = 'none';
            overlay.style.pointerEvents = 'none';
            // Remove from DOM to ensure they don't block clicks
            overlay.remove();
          }
        });
        
        // ✅ ADDITIONAL SAFETY: Remove any elements with high z-index that might be blocking
        // Check for any fixed-position elements with z-index >= 99999 that might be leftover overlays
        const allFixedElements = document.querySelectorAll('[style*="position: fixed"], [style*="position:fixed"]');
        allFixedElements.forEach(el => {
          const style = window.getComputedStyle(el);
          const zIndex = parseInt(style.zIndex) || 0;
          if (zIndex >= 99999 && el.id && el.id.includes('overlay')) {
            console.warn(`⚠️ Campaign: Found leftover overlay ${el.id} with z-index ${zIndex} - removing`);
            el.style.display = 'none';
            el.style.pointerEvents = 'none';
            el.remove();
          }
        });
      };
      
      // Remove immediately
      removeAllOverlays();
      
      // Continuously check and remove (in case other useEffects recreate them)
      // Run more frequently to catch any overlays that get recreated
      const cleanupInterval = setInterval(removeAllOverlays, 50);
      
      console.log('✅ Campaign: Tutorial ended - continuously removing all blocking overlays');
      
      return () => {
        clearInterval(cleanupInterval);
        // Final cleanup on unmount
        removeAllOverlays();
      };
    }
  }, [currentTutorial]);

  // Block both front zones and Keep button during "Counter Strike" tutorial (only allow flank)
  useEffect(() => {
    // ✅ CRITICAL: If no tutorial is active, remove all blocking overlays and return early
    if (!currentTutorial) {
      ['counter-strike-player-front-block-overlay', 'counter-strike-opponent-front-block-overlay',
       'counter-strike-opponent-flank-block-overlay', 'counter-strike-opponent-general-block-overlay',
       'counter-strike-player-general-block-overlay', 'counter-strike-keep-button-block-overlay'].forEach(id => {
        const el = document.getElementById(id);
        if (el) {
          el.style.display = 'none';
          el.remove();
        }
      });
      return;
    }
    
    const isCounterStrikeTutorial = currentTutorial?.stepId === 'counter_strike';
    const isStrikeTheBerserkerTutorial = currentTutorial?.stepId === 'strike_the_berserker';
    const isDefeatTutorial = currentTutorial?.stepId === 'defeat_your_opponent';
    const isTutorialComplete = !currentTutorial || isDefeatTutorial; // After final tutorial, unlock everything
    const shouldBlockCounterStrike = !isTutorialComplete && (isCounterStrikeTutorial || isStrikeTheBerserkerTutorial);
    
    // Get or create blocking overlay for player's front zone
    let playerFrontBlockOverlay = document.getElementById('counter-strike-player-front-block-overlay');
    if (!playerFrontBlockOverlay) {
      playerFrontBlockOverlay = document.createElement('div');
      playerFrontBlockOverlay.id = 'counter-strike-player-front-block-overlay';
      playerFrontBlockOverlay.style.position = 'fixed';
      playerFrontBlockOverlay.style.zIndex = '99999';
      playerFrontBlockOverlay.style.pointerEvents = 'auto';
      playerFrontBlockOverlay.style.backgroundColor = 'transparent';
      document.body.appendChild(playerFrontBlockOverlay);
    }
    
    // Get or create blocking overlay for opponent's front zone
    let opponentFrontBlockOverlay = document.getElementById('counter-strike-opponent-front-block-overlay');
    if (!opponentFrontBlockOverlay) {
      opponentFrontBlockOverlay = document.createElement('div');
      opponentFrontBlockOverlay.id = 'counter-strike-opponent-front-block-overlay';
      opponentFrontBlockOverlay.style.position = 'fixed';
      opponentFrontBlockOverlay.style.zIndex = '99999';
      opponentFrontBlockOverlay.style.pointerEvents = 'auto';
      opponentFrontBlockOverlay.style.backgroundColor = 'transparent';
      document.body.appendChild(opponentFrontBlockOverlay);
    }
    
    // Get or create blocking overlay for Keep button
    let keepButtonBlockOverlay = document.getElementById('counter-strike-keep-button-block-overlay');
    if (!keepButtonBlockOverlay) {
      keepButtonBlockOverlay = document.createElement('div');
      keepButtonBlockOverlay.id = 'counter-strike-keep-button-block-overlay';
      keepButtonBlockOverlay.style.position = 'fixed';
      keepButtonBlockOverlay.style.zIndex = '100002'; // Higher than counter strike dialog (1000) and tutorial (100000)
      keepButtonBlockOverlay.style.pointerEvents = 'auto';
      keepButtonBlockOverlay.style.backgroundColor = 'transparent';
      document.body.appendChild(keepButtonBlockOverlay);
    }
    
    // Mission 2 "Strike the Berserker": also need opponent flank and opponent general block overlays
    let opponentFlankBlockOverlay = document.getElementById('counter-strike-opponent-flank-block-overlay');
    if (!opponentFlankBlockOverlay) {
      opponentFlankBlockOverlay = document.createElement('div');
      opponentFlankBlockOverlay.id = 'counter-strike-opponent-flank-block-overlay';
      opponentFlankBlockOverlay.style.position = 'fixed';
      opponentFlankBlockOverlay.style.zIndex = '99999';
      opponentFlankBlockOverlay.style.pointerEvents = 'auto';
      opponentFlankBlockOverlay.style.backgroundColor = 'transparent';
      document.body.appendChild(opponentFlankBlockOverlay);
    }
    let opponentGeneralBlockOverlay = document.getElementById('counter-strike-opponent-general-block-overlay');
    if (!opponentGeneralBlockOverlay) {
      opponentGeneralBlockOverlay = document.createElement('div');
      opponentGeneralBlockOverlay.id = 'counter-strike-opponent-general-block-overlay';
      opponentGeneralBlockOverlay.style.position = 'fixed';
      opponentGeneralBlockOverlay.style.zIndex = '99999';
      opponentGeneralBlockOverlay.style.pointerEvents = 'auto';
      opponentGeneralBlockOverlay.style.backgroundColor = 'transparent';
      document.body.appendChild(opponentGeneralBlockOverlay);
    }
    let playerGeneralBlockOverlay = document.getElementById('counter-strike-player-general-block-overlay');
    if (!playerGeneralBlockOverlay) {
      playerGeneralBlockOverlay = document.createElement('div');
      playerGeneralBlockOverlay.id = 'counter-strike-player-general-block-overlay';
      playerGeneralBlockOverlay.style.position = 'fixed';
      playerGeneralBlockOverlay.style.zIndex = '99999';
      playerGeneralBlockOverlay.style.pointerEvents = 'auto';
      playerGeneralBlockOverlay.style.backgroundColor = 'transparent';
      document.body.appendChild(playerGeneralBlockOverlay);
    }

    const updateOverlays = () => {
      // ✅ FIX: Explicitly hide all blocking overlays during "Defeat your opponent" tutorial
      if (isDefeatTutorial) {
        if (playerFrontBlockOverlay) playerFrontBlockOverlay.style.display = 'none';
        if (opponentFrontBlockOverlay) opponentFrontBlockOverlay.style.display = 'none';
        if (opponentFlankBlockOverlay) opponentFlankBlockOverlay.style.display = 'none';
        if (opponentGeneralBlockOverlay) opponentGeneralBlockOverlay.style.display = 'none';
        if (playerGeneralBlockOverlay) playerGeneralBlockOverlay.style.display = 'none';
        if (keepButtonBlockOverlay) keepButtonBlockOverlay.style.display = 'none';
        return;
      }

      // Mission 1 "Counter Strike": block player front, opponent front, Keep (allow flank for Missile Barrage)
      // Mission 2 "Strike the Berserker": block Keep, opponent flank, opponent general (allow opponent front for Malignant Berserker)
      
      // Update player's front zone overlay (mission 1 only)
      if (shouldBlockCounterStrike && isCounterStrikeTutorial && playerFrontBlockOverlay) {
        const frontZones = document.querySelectorAll('.battle-zone.front');
        const playerFrontZone = Array.from(frontZones).find(zone => !zone.classList.contains('opponent-front'));
        if (playerFrontZone) {
          const rect = playerFrontZone.getBoundingClientRect();
          playerFrontBlockOverlay.style.display = 'block';
          playerFrontBlockOverlay.style.left = `${rect.left}px`;
          playerFrontBlockOverlay.style.top = `${rect.top}px`;
          playerFrontBlockOverlay.style.width = `${rect.width}px`;
          playerFrontBlockOverlay.style.height = `${rect.height}px`;
        } else {
          playerFrontBlockOverlay.style.display = 'none';
        }
      } else if (playerFrontBlockOverlay) {
        playerFrontBlockOverlay.style.display = 'none';
      }

      // Update opponent's front zone overlay (mission 1 only - mission 2 needs it clickable for Malignant Berserker)
      if (shouldBlockCounterStrike && isCounterStrikeTutorial && opponentFrontBlockOverlay) {
        const opponentFrontZone = document.querySelector('.battle-zone.opponent-front');
        if (opponentFrontZone) {
          const rect = opponentFrontZone.getBoundingClientRect();
          opponentFrontBlockOverlay.style.display = 'block';
          opponentFrontBlockOverlay.style.left = `${rect.left}px`;
          opponentFrontBlockOverlay.style.top = `${rect.top}px`;
          opponentFrontBlockOverlay.style.width = `${rect.width}px`;
          opponentFrontBlockOverlay.style.height = `${rect.height}px`;
        } else {
          opponentFrontBlockOverlay.style.display = 'none';
        }
      } else if (opponentFrontBlockOverlay) {
        opponentFrontBlockOverlay.style.display = 'none';
      }

      // Update opponent's flank zone overlay (mission 2 only - block flank so only front/Berserker is targetable)
      if (shouldBlockCounterStrike && isStrikeTheBerserkerTutorial && opponentFlankBlockOverlay) {
        const opponentFlankZone = document.querySelector('.battle-zone.opponent-flank');
        if (opponentFlankZone) {
          const rect = opponentFlankZone.getBoundingClientRect();
          opponentFlankBlockOverlay.style.display = 'block';
          opponentFlankBlockOverlay.style.left = `${rect.left}px`;
          opponentFlankBlockOverlay.style.top = `${rect.top}px`;
          opponentFlankBlockOverlay.style.width = `${rect.width}px`;
          opponentFlankBlockOverlay.style.height = `${rect.height}px`;
        } else {
          opponentFlankBlockOverlay.style.display = 'none';
        }
      } else if (opponentFlankBlockOverlay) {
        opponentFlankBlockOverlay.style.display = 'none';
      }

      // Update opponent general overlay (mission 2 only - block general so only Malignant Berserker is targetable)
      if (shouldBlockCounterStrike && isStrikeTheBerserkerTutorial && opponentGeneralBlockOverlay) {
        const generalEl = document.querySelector('[data-zone="opponent_general"]');
        if (generalEl) {
          const rect = generalEl.getBoundingClientRect();
          opponentGeneralBlockOverlay.style.display = 'block';
          opponentGeneralBlockOverlay.style.left = `${rect.left}px`;
          opponentGeneralBlockOverlay.style.top = `${rect.top}px`;
          opponentGeneralBlockOverlay.style.width = `${rect.width}px`;
          opponentGeneralBlockOverlay.style.height = `${rect.height}px`;
        } else {
          opponentGeneralBlockOverlay.style.display = 'none';
        }
      } else if (opponentGeneralBlockOverlay) {
        opponentGeneralBlockOverlay.style.display = 'none';
      }

      // Update player general overlay (mission 2 only - block player general targeting)
      if (shouldBlockCounterStrike && isStrikeTheBerserkerTutorial && playerGeneralBlockOverlay) {
        const playerGeneralEl = document.querySelector('[data-zone="player_general"]');
        if (playerGeneralEl) {
          const rect = playerGeneralEl.getBoundingClientRect();
          playerGeneralBlockOverlay.style.display = 'block';
          playerGeneralBlockOverlay.style.left = `${rect.left}px`;
          playerGeneralBlockOverlay.style.top = `${rect.top}px`;
          playerGeneralBlockOverlay.style.width = `${rect.width}px`;
          playerGeneralBlockOverlay.style.height = `${rect.height}px`;
        } else {
          playerGeneralBlockOverlay.style.display = 'none';
        }
      } else if (playerGeneralBlockOverlay) {
        playerGeneralBlockOverlay.style.display = 'none';
      }
      
      // Update Keep button overlay
      if (shouldBlockCounterStrike && keepButtonBlockOverlay) {
        const keepButton = document.querySelector('.counter-strike-keep-button');
        if (keepButton) {
          const rect = keepButton.getBoundingClientRect();
          keepButtonBlockOverlay.style.display = 'block';
          keepButtonBlockOverlay.style.left = `${rect.left}px`;
          keepButtonBlockOverlay.style.top = `${rect.top}px`;
          keepButtonBlockOverlay.style.width = `${rect.width}px`;
          keepButtonBlockOverlay.style.height = `${rect.height}px`;
        } else {
          keepButtonBlockOverlay.style.display = 'none';
        }
      } else if (keepButtonBlockOverlay) {
        keepButtonBlockOverlay.style.display = 'none';
      }
    };
    
    updateOverlays();
    
    // Continuously update overlay positions
    let updateInterval = null;
    if (shouldBlockCounterStrike) {
      updateInterval = setInterval(updateOverlays, 100);
    }
    
    const handleResize = () => {
      if (shouldBlockCounterStrike) {
        updateOverlays();
      }
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    
    return () => {
      if (updateInterval) {
        clearInterval(updateInterval);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
      if (playerFrontBlockOverlay) {
        playerFrontBlockOverlay.style.display = 'none';
      }
      if (opponentFrontBlockOverlay) opponentFrontBlockOverlay.style.display = 'none';
      if (opponentFlankBlockOverlay) opponentFlankBlockOverlay.style.display = 'none';
      if (opponentGeneralBlockOverlay) opponentGeneralBlockOverlay.style.display = 'none';
      if (playerGeneralBlockOverlay) playerGeneralBlockOverlay.style.display = 'none';
      if (keepButtonBlockOverlay) keepButtonBlockOverlay.style.display = 'none';
    };
  }, [currentTutorial]);

  // Strike the Berserker: set body attr to hide general targeting highlights via CSS
  useEffect(() => {
    const active = currentTutorial?.stepId === 'strike_the_berserker';
    if (active) {
      document.body.setAttribute('data-strike-berserker-tutorial', 'true');
    } else {
      document.body.removeAttribute('data-strike-berserker-tutorial');
    }
    return () => document.body.removeAttribute('data-strike-berserker-tutorial');
  }, [currentTutorial?.title]);

  // Mission 3: hide flank targeting visualization during tutorials that block flank deployment
  useEffect(() => {
    const active = currentTutorial?.stepId === 'destroy_the_drone' ||
                   currentTutorial?.stepId === 'deploy_effects' ||
                   currentTutorial?.stepId === 'samuel_johnson';
    if (active) {
      document.body.setAttribute('data-block-flank-tutorial', 'true');
    } else {
      document.body.removeAttribute('data-block-flank-tutorial');
    }
    return () => document.body.removeAttribute('data-block-flank-tutorial');
  }, [currentTutorial?.title]);

  // Mission 3: hide front targeting visualization when front zone is blocked (Missile Barrage only)
  // Do NOT set for "Protect Your Units" — front zone must stay interactive so Samuel/Kek can be selected to attack
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_3') return;
    const isMissileBarrageDialog = currentTutorial?.stepId === 'missile_barrage' && !suppressTutorialDialog;
    if (suppressTutorialDialog || isMissileBarrageDialog) {
      document.body.setAttribute('data-block-front-tutorial', 'true');
    } else {
      document.body.removeAttribute('data-block-front-tutorial');
    }
    return () => document.body.removeAttribute('data-block-front-tutorial');
  }, [missionId, suppressTutorialDialog, currentTutorial?.title]);

  // Mission 3: Kek (239) targeting restriction
  // - "Confuse": target any enemy unit (Sankrat Drill Sergeant) — no attribute
  // - "Destroy the Drone" / "Deploy Effects": drone-only (data-kek-drone-only)
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_3') return;
    const t = currentTutorial?.stepId || currentTutorial?.title;
    const kekDroneOnlyPhase = t === 'Destroy the Drone' || t === 'Deploy Effects';
    if (kekDroneOnlyPhase) {
      document.body.setAttribute('data-kek-drone-only', 'true');
    } else {
      document.body.removeAttribute('data-kek-drone-only');
    }
    return () => document.body.removeAttribute('data-kek-drone-only');
  }, [missionId, currentTutorial?.title]);

  // Mission 3: Drone targeting restriction - only allow Dark Circuit Engineer (47) during "Use Your Drone"
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_3') return;
    if (currentTutorial?.stepId === 'use_your_drone') {
      document.body.setAttribute('data-drone-target-only', '47');
    } else {
      document.body.removeAttribute('data-drone-target-only');
    }
    return () => document.body.removeAttribute('data-drone-target-only');
  }, [missionId, currentTutorial?.title]);

  // Mission 3: Block drone activation during "Your Drones" (player must only deploy the drone, then pass)
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_3') return;
    if (currentTutorial?.stepId === 'your_drones') {
      document.body.setAttribute('data-tutorial-block-drone-activation', 'true');
    } else {
      document.body.removeAttribute('data-tutorial-block-drone-activation');
    }
    return () => document.body.removeAttribute('data-tutorial-block-drone-activation');
  }, [missionId, currentTutorial?.title]);

  // Mission 2 "Kill threats": block opponent general so player must attack Commander of the Hollow
  useEffect(() => {
    if (!currentTutorial) {
      const overlay = document.getElementById('kill-threats-general-block-overlay');
      if (overlay) { overlay.style.display = 'none'; overlay.remove(); }
      return;
    }
    const isKillThreats = currentTutorial.title === "Kill threats";
    if (!isKillThreats) {
      const overlay = document.getElementById('kill-threats-general-block-overlay');
      if (overlay) overlay.style.display = 'none';
      return;
    }

    let overlay = document.getElementById('kill-threats-general-block-overlay');
    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'kill-threats-general-block-overlay';
      overlay.style.position = 'fixed';
      overlay.style.zIndex = '99999';
      overlay.style.pointerEvents = 'auto';
      overlay.style.backgroundColor = 'transparent';
      document.body.appendChild(overlay);
    }

    const updateOverlay = () => {
      const generalEl = document.querySelector('[data-zone="opponent_general"]');
      if (generalEl) {
        const rect = generalEl.getBoundingClientRect();
        overlay.style.display = 'block';
        overlay.style.left = `${rect.left}px`;
        overlay.style.top = `${rect.top}px`;
        overlay.style.width = `${rect.width}px`;
        overlay.style.height = `${rect.height}px`;
      } else {
        overlay.style.display = 'none';
      }
    };

    updateOverlay();
    const iv = setInterval(updateOverlay, 100);
    const handleResize = () => updateOverlay();
    window.addEventListener('resize', handleResize);

    return () => {
      clearInterval(iv);
      window.removeEventListener('resize', handleResize);
      if (overlay) overlay.style.display = 'none';
    };
  }, [currentTutorial]);

  // Block Isabela (500) and Tranquilizer Blaster (333) during "Overwhelming force" tutorial (only allow David)
  useEffect(() => {
    // ✅ CRITICAL: If no tutorial is active, remove blocking overlays and return early
    if (!currentTutorial) {
      const isabelaBlockOverlay = document.getElementById('isabela-block-overlay');
      const tranquilizerBlockOverlay = document.getElementById('tranquilizer-block-overlay');
      if (isabelaBlockOverlay) {
        isabelaBlockOverlay.style.display = 'none';
        isabelaBlockOverlay.remove();
      }
      if (tranquilizerBlockOverlay) {
        tranquilizerBlockOverlay.style.display = 'none';
        tranquilizerBlockOverlay.remove();
      }
      return;
    }
    
    const isOverwhelmingTutorial = currentTutorial?.stepId === 'overwhelming_force';
    const isDefeatTutorial = currentTutorial?.stepId === 'defeat_your_opponent';
    const isTutorialComplete = !currentTutorial || isDefeatTutorial;
    const shouldBlockOrders = !isTutorialComplete && isOverwhelmingTutorial;
    
    // Get or create blocking overlays for blocked orders
    let isabelaBlockOverlay = document.getElementById('isabela-block-overlay');
    if (!isabelaBlockOverlay) {
      isabelaBlockOverlay = document.createElement('div');
      isabelaBlockOverlay.id = 'isabela-block-overlay';
      isabelaBlockOverlay.style.position = 'fixed';
      isabelaBlockOverlay.style.zIndex = '99999';
      isabelaBlockOverlay.style.pointerEvents = 'auto';
      isabelaBlockOverlay.style.backgroundColor = 'rgba(0, 0, 0, 0.3)';
      isabelaBlockOverlay.style.cursor = 'not-allowed';
      document.body.appendChild(isabelaBlockOverlay);
    }
    
    let tranquilizerBlockOverlay = document.getElementById('tranquilizer-block-overlay');
    if (!tranquilizerBlockOverlay) {
      tranquilizerBlockOverlay = document.createElement('div');
      tranquilizerBlockOverlay.id = 'tranquilizer-block-overlay';
      tranquilizerBlockOverlay.style.position = 'fixed';
      tranquilizerBlockOverlay.style.zIndex = '99999';
      tranquilizerBlockOverlay.style.pointerEvents = 'auto';
      tranquilizerBlockOverlay.style.backgroundColor = 'rgba(0, 0, 0, 0.3)';
      tranquilizerBlockOverlay.style.cursor = 'not-allowed';
      document.body.appendChild(tranquilizerBlockOverlay);
    }
    
    const updateOverlays = () => {
      if (!shouldBlockOrders) {
        // Hide overlays when not blocking
        if (isabelaBlockOverlay) {
          isabelaBlockOverlay.style.display = 'none';
        }
        if (tranquilizerBlockOverlay) {
          tranquilizerBlockOverlay.style.display = 'none';
        }
        return;
      }
      
      // Find order carousel
      const orderCarousel = document.querySelector('.order-carousel');
      if (!orderCarousel) {
        // Carousel not found, hide overlays
        if (isabelaBlockOverlay) {
          isabelaBlockOverlay.style.display = 'none';
        }
        if (tranquilizerBlockOverlay) {
          tranquilizerBlockOverlay.style.display = 'none';
        }
        return;
      }
      
      // Find Isabela (order 500) in carousel
      const isabelaCard = orderCarousel.querySelector('[data-order-id="500"]') || 
                          Array.from(orderCarousel.querySelectorAll('.carousel-order-card')).find(card => 
                            card.textContent?.includes('Isabela')
                          );
      
      if (isabelaCard && isabelaBlockOverlay) {
        const rect = isabelaCard.getBoundingClientRect();
        isabelaBlockOverlay.style.display = 'block';
        isabelaBlockOverlay.style.left = `${rect.left}px`;
        isabelaBlockOverlay.style.top = `${rect.top}px`;
        isabelaBlockOverlay.style.width = `${rect.width}px`;
        isabelaBlockOverlay.style.height = `${rect.height}px`;
      } else if (isabelaBlockOverlay) {
        isabelaBlockOverlay.style.display = 'none';
      }
      
      // Find Tranquilizer Blaster (order 333) in carousel
      const tranquilizerCard = orderCarousel.querySelector('[data-order-id="333"]') || 
                               Array.from(orderCarousel.querySelectorAll('.carousel-order-card')).find(card => 
                                 card.textContent?.includes('Tranquilizer') || card.textContent?.includes('Blaster')
                               );
      
      if (tranquilizerCard && tranquilizerBlockOverlay) {
        const rect = tranquilizerCard.getBoundingClientRect();
        tranquilizerBlockOverlay.style.display = 'block';
        tranquilizerBlockOverlay.style.left = `${rect.left}px`;
        tranquilizerBlockOverlay.style.top = `${rect.top}px`;
        tranquilizerBlockOverlay.style.width = `${rect.width}px`;
        tranquilizerBlockOverlay.style.height = `${rect.height}px`;
      } else if (tranquilizerBlockOverlay) {
        tranquilizerBlockOverlay.style.display = 'none';
      }
    };
    
    // Update immediately
    updateOverlays();
    
    // Continuously update overlay positions
    let updateInterval = null;
    if (shouldBlockOrders) {
      updateInterval = setInterval(updateOverlays, 100);
    }
    
    // Also update on window resize/scroll
    const handleResize = () => {
      if (shouldBlockOrders) {
        updateOverlays();
      }
    };
    window.addEventListener('resize', handleResize);
    window.addEventListener('scroll', handleResize);
    
    return () => {
      if (updateInterval) {
        clearInterval(updateInterval);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('scroll', handleResize);
      if (isabelaBlockOverlay) {
        isabelaBlockOverlay.style.display = 'none';
      }
      if (tranquilizerBlockOverlay) {
        tranquilizerBlockOverlay.style.display = 'none';
      }
    };
  }, [currentTutorial]);

  // Auto-advance "Counter Strike" tutorial when Missile Barrage is in completed orders
  useEffect(() => {
    const isCounterStrikeTutorial = currentTutorial?.stepId === 'counter_strike';
    if (!isCounterStrikeTutorial) {
      counterStrikeTutorialAdvancedRef.current = false;
      return;
    }
    
    if (counterStrikeTutorialAdvancedRef.current) return;
    
    // Check completed orders for Missile Barrage (order ID 316)
    const checkCompletedOrders = () => {
      if (counterStrikeTutorialAdvancedRef.current) return;
      
      // Check ActionHistory DOM for Missile Barrage deployments
      // ActionHistory shows when orders are played and moved to completed orders
      const actionHistory = document.querySelector('.action-history, [class*="action-history"], [class*="ActionHistory"]');
      if (actionHistory) {
        const historyText = actionHistory.textContent || '';
        const historyHTML = actionHistory.innerHTML || '';
        
        // Count occurrences of "Missile Barrage" or "Missle Barrage" (database has typo)
        // This indicates it was deployed and moved to completed orders
        // Note: Database has "Missle" (typo) but we check for both spellings
        const textMatches = historyText.match(/(Missile|Missle) Barrage/g);
        const htmlMatches = historyHTML.match(/(Missile|Missle) Barrage/g);
        const missileBarrageCount = Math.max(
          textMatches ? textMatches.length : 0,
          htmlMatches ? htmlMatches.length : 0
        );
        
        if (missileBarrageCount >= 1 && !counterStrikeTutorialAdvancedRef.current) {
          counterStrikeTutorialAdvancedRef.current = true;
          console.log(`✅ Counter Strike tutorial: Found ${missileBarrageCount} Missile Barrage in completed orders (ActionHistory) - advancing to next tutorial`);
          continueTutorial();
          return true;
        }
      }
      
      // Also check for any elements with order ID 316 in completed orders area
      const completedOrdersElements = document.querySelectorAll('[data-order-id="316"], [data-order-id="316"]');
      if (completedOrdersElements.length >= 1 && !counterStrikeTutorialAdvancedRef.current) {
        counterStrikeTutorialAdvancedRef.current = true;
        console.log(`✅ Counter Strike tutorial: Found Missile Barrage (ID 316) in completed orders area - advancing to next tutorial`);
        continueTutorial();
        return true;
      }
      
      return false;
    };
    
    // Poll every 200ms to check completed orders
    const pollInterval = setInterval(() => {
      checkCompletedOrders();
    }, 200);
    
    return () => {
      clearInterval(pollInterval);
    };
  }, [currentTutorial, continueTutorial]);

  // Auto-advance "Strike the Berserker" only when Precision Strike (321) targeting is resolved (not when they start targeting)
  // Advancing on DOM/action history caused config to clear mid-targeting (cancel reappeared, generals became targetable)
  useEffect(() => {
    const isStrikeTheBerserkerTutorial = currentTutorial?.stepId === 'strike_the_berserker';
    if (!isStrikeTheBerserkerTutorial) {
      strikeTheBerserkerAdvancedRef.current = false;
      strikeTheBerserkerPrevNeedsRef.current = null;
      return;
    }
    if (strikeTheBerserkerAdvancedRef.current) return;

    const uid = userId || (typeof localStorage !== 'undefined' ? parseInt(localStorage.getItem('userId'), 10) : null);
    const playerState = uid && gameState?.state?.[String(uid)];
    const needsNow = playerState?.needsStrategemTarget;
    const strategemIdNow = needsNow?.strategemId != null ? needsNow.strategemId : null;

    const prevId = strikeTheBerserkerPrevNeedsRef.current;
    strikeTheBerserkerPrevNeedsRef.current = strategemIdNow;

    if (prevId === 321 && (strategemIdNow == null || strategemIdNow !== 321)) {
      strikeTheBerserkerAdvancedRef.current = true;
      console.log('✅ Strike the Berserker tutorial: Precision Strike targeting resolved - advancing');
      continueTutorial();
    }
  }, [currentTutorial, continueTutorial, gameState?.state, userId]);

  // Auto-advance "Opponent's turn" when Opportunist (302) is deployed to flank (show next tutorial)
  useEffect(() => {
    if (currentTutorial?.stepId !== 'opponents_turn') return;
    
    let hasAdvanced = false; // Prevent multiple advances
    
    const handleOpportunistDeployed = (event) => {
      if (hasAdvanced) return;
      hasAdvanced = true;
      const backendQueue = event.detail?.gameState?.scriptState?.tutorialQueue || [];
      let nextTutorial = event.detail?.nextTutorial;
      if (!nextTutorial || nextTutorial.stepId === 'opponents_turn') {
        nextTutorial = backendQueue.find(t => (t.stepId || t.title || t.id) === 'special_units');
      }
      if (!nextTutorial && missionScript) {
        const action = missionScript.scriptedActions.find(
          a => a.trigger === 'tutorial' && a.explanation?.stepId === 'special_units'
        );
        nextTutorial = action?.explanation;
      }
      if (!nextTutorial) {
        nextTutorial = { stepId: 'special_units' };
      }
      console.log('✅ Opportunist deployed to flank - advancing to next tutorial', nextTutorial?.stepId || nextTutorial?.title);
      advanceToTutorial(nextTutorial);
    };
    
    // Listen for event from BattleUI (fires when gameState has Opportunist in flank)
    window.addEventListener('opportunist-deployed-to-flank', handleOpportunistDeployed);
    
    return () => {
      window.removeEventListener('opportunist-deployed-to-flank', handleOpportunistDeployed);
    };
  }, [currentTutorial, advanceToTutorial, missionScript]);

  // Auto-advance "Out of reach" (general only) or "Attack the general" (any Opportunist attack vs opponent)
  // (Guided Missile destroying Panicked Overseer is handled elsewhere -> shows "Attack the general")
  useEffect(() => {
    const isAttackingTutorial = currentTutorial?.stepId === 'out_of_reach' || currentTutorial?.stepId === 'attack_the_general';
    if (!isAttackingTutorial) {
      attackingTutorialAdvancedRef.current = false;
      return;
    }

    const handleAttackComplete = (event) => {
      const { attackOutcome } = event.detail;
      if (!attackOutcome) return;
      const stepId = currentTutorial?.stepId;
      const att = attackOutcome.attacker;
      if (!att || Number(att.id) !== 302) return;
      if (!attackOutcome.targetPlayerId || !attackOutcome.attackerPlayerId) return;
      const isPlayerAttackingOpponent = attackOutcome.attackerPlayerId !== attackOutcome.targetPlayerId;
      if (!isPlayerAttackingOpponent) return;

      if (stepId === 'attack_the_general') {
        if (!attackingTutorialAdvancedRef.current) {
          attackingTutorialAdvancedRef.current = true;
          console.log('✅ attack_the_general: Opportunist (302) attacked an opponent target — advancing tutorial');
          continueTutorial();
        }
        return;
      }
      if (stepId === 'out_of_reach' && attackOutcome.isGeneralAttack === true) {
        if (!attackingTutorialAdvancedRef.current) {
          attackingTutorialAdvancedRef.current = true;
          console.log('✅ out_of_reach tutorial: Opportunist attacked opponent general — advancing tutorial');
          continueTutorial();
        }
      }
    };

    window.addEventListener('attack-complete', handleAttackComplete);

    return () => {
      window.removeEventListener('attack-complete', handleAttackComplete);
    };
  }, [currentTutorial, continueTutorial]);

  // "Attack the general": zZz / hasAttacked from state without relying on attack-complete ordering
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_1' || currentTutorial?.stepId !== 'attack_the_general') return;
    if (attackingTutorialAdvancedRef.current) return;
    if (!opportunist302AllowsPassTurnDuringAttackGeneralStep(gameState, userId)) return;
    attackingTutorialAdvancedRef.current = true;
    continueTutorial();
  }, [missionId, currentTutorial?.stepId, gameState, userId, continueTutorial]);

  // Show "Opponent's turn" tutorial when Blackguard Operative (order 294) dies from bot attack
  useEffect(() => {
    if (blackguardOperativeDeathTutorialShownRef.current || !missionScript) return;
    
    const handleBlackguardDeath = (event) => {
      const { attackOutcome } = event.detail;
      if (!attackOutcome || blackguardOperativeDeathTutorialShownRef.current) return;
      
      const isBotAttack = attackOutcome.attackerPlayerId === 'campaign_bot';
      const targetWasBlackguard = attackOutcome.target?.id === 294;
      const targetDestroyed = attackOutcome.targetDestroyed === true;
      
      if (isBotAttack && targetWasBlackguard && targetDestroyed) {
        blackguardOperativeDeathTutorialShownRef.current = true;
        const deathTutorial = missionScript.scriptedActions.find(
          a => a.trigger === 'after_unit_attacked' && a.targetUnit?.orderId === 294
        )?.targetDestroyedExplanation;
        if (deathTutorial) {
          addTutorial(deathTutorial);
        } else {
          addTutorial({
            title: "Opponent's turn",
            text: "Both Infected attacked your Blackguard Operative. Attacking and defending works the same way. Both units involved always deal their damage and take the damage they are dealt. This means both units normally take damage and often one or both die. Your opponent will soon deploy reinforcements but first it's our turn. Deploy your Opportunist in hand into your flank."
          });
        }
      }
    };
    
    window.addEventListener('attack-complete', handleBlackguardDeath);
    return () => window.removeEventListener('attack-complete', handleBlackguardDeath);
  }, [missionScript, addTutorial]);

  // Track if turn 2 and turn 3 tutorials have been queued
  const turn2TutorialsQueuedRef = useRef(false);
  const turn3TutorialsQueuedRef = useRef(false);
  const turn4FinishThemQueuedRef = useRef(false);
  const counterStrikeTutorialQueuedRef = useRef(false);
  const mission2CounterStrikeTutorialQueuedRef = useRef(false);
  const endTurnTutorialCompletedRef = useRef(false);
  const lastOpponentHealthRef = useRef(null);
  const attackingTutorialAdvancedRef = useRef(false);
  const counterStrikeTutorialAdvancedRef = useRef(false);
  const strikeTheBerserkerAdvancedRef = useRef(false);
  const strikeTheBerserkerPrevNeedsRef = useRef(null);
  const blackguardOperativeDeathTutorialShownRef = useRef(false);
  const hadPanickedOverseerInOpponentFlankRef = useRef(false);
  
  const attackTheGeneralTutorial = {
    stepId: "attack_the_general",
    title: "Attack the general",
    text: "Attack the opponent general now that Opportunist has 3 attack again. Click your Opportunist and then click the opponent's general. After that you can pass the turn."
  };

  // Auto-advance "Out of reach" when Panicked Overseer (23) is destroyed in opponent's flank
  // Show "Attack the general" tutorial instead of advancing directly to "End turn"
  useEffect(() => {
    if (currentTutorial?.stepId !== 'out_of_reach') { hadPanickedOverseerInOpponentFlankRef.current = false; return; }
    if (!gameState?.state || !gameState?.campaignMode) return;
    const opponentId = gameState.players?.find(p => p === 'campaign_bot');
    if (!opponentId) return;
    const flank = gameState.state[opponentId]?.playedOrders?.flank || [];
    const has = flank.some(u => u && u.id === 23);
    if (has) hadPanickedOverseerInOpponentFlankRef.current = true;
    else if (hadPanickedOverseerInOpponentFlankRef.current) {
      hadPanickedOverseerInOpponentFlankRef.current = false;
      attackingTutorialAdvancedRef.current = false;
      advanceToTutorial(attackTheGeneralTutorial);
    }
  }, [gameState, currentTutorial, addTutorial, advanceToTutorial]);
  
  // Mark when "End turn" tutorial completes
  useEffect(() => {
    if ((currentTutorial?.stepId === 'end_turn_1' || currentTutorial?.stepId === 'end_turn_2')) {
      endTurnTutorialCompletedRef.current = false;
    } else if (!currentTutorial && endTurnTutorialCompletedRef.current === false) {
      // End turn tutorial just completed
      endTurnTutorialCompletedRef.current = true;
    }
  }, [currentTutorial]);
  
  // After "End turn" tutorial completes, wait for bot's turn to finish, then add turn 2 tutorials
  useEffect(() => {
    // Early return if conditions not met OR if already queued (prevent re-running)
    if (!endTurnTutorialCompletedRef.current || turn2TutorialsQueuedRef.current || !missionScript) {
      return;
    }
    
    // Poll to detect when turn 2 starts (player's turn again + Forsaken Sentinel deployed)
    const checkForTurn2 = () => {
      // Double-check ref hasn't changed (prevent race conditions)
      if (turn2TutorialsQueuedRef.current) {
        return;
      }
      
      // Check if opponent has deployed Forsaken Sentinel (happens on bot's turn 1)
      const opponentFrontZone = document.querySelector('.battle-zone.opponent-front');
      if (!opponentFrontZone) return;
      
      const hasForsakenSentinel = opponentFrontZone.textContent?.includes('Forsaken Sentinel') ||
                                  opponentFrontZone.textContent?.includes('Sentinel');
      
      // Check if it's player's turn (end turn button is enabled/visible)
      const endTurnButton = document.querySelector('.end-turn-button');
      const isPlayerTurn = endTurnButton && 
                          !endTurnButton.disabled && 
                          endTurnButton.style.pointerEvents !== 'none';
      
      // If it's player's turn and Forsaken Sentinel is present, we're on turn 2
      if (isPlayerTurn && hasForsakenSentinel && !turn2TutorialsQueuedRef.current) {
        // Set ref immediately to prevent multiple triggers
        turn2TutorialsQueuedRef.current = true;
        
        const turn2Tutorials = missionScript.scriptedActions.filter(action => {
          return action.trigger === 'tutorial' && 
                 action.turn === 2 && 
                 action.player === 'player';
        });
        
        if (turn2Tutorials.length > 0) {
          console.log(`📚 Campaign: Detected turn 2, adding ${turn2Tutorials.length} tutorial(s) to queue`);
          console.log(`📚 Campaign: Current tutorial: ${currentTutorial?.stepId || currentTutorial?.title || 'none'}`);
          console.log(`📚 Campaign: Turn 2 tutorials to add:`, turn2Tutorials.map(a => a.explanation.stepId || a.explanation.title));
          
          // Add tutorials directly to queue without touching gameState
          turn2Tutorials.forEach(action => {
            console.log(`📚 Campaign: Adding tutorial "${action.explanation.stepId || action.explanation.title}" to queue`);
            addTutorial(action.explanation);
          });
        }
      }
    };
    
    // Poll every 500ms to check for turn 2
    const pollInterval = setInterval(checkForTurn2, 500);
    
    return () => {
      clearInterval(pollInterval);
    };
  }, [endTurnTutorialCompletedRef.current, missionScript, addTutorial, currentTutorial]);

  // After turn 2 tutorials complete, wait for bot's turn to finish, then add turn 3 tutorials
  useEffect(() => {
    if (!turn2TutorialsQueuedRef.current || turn3TutorialsQueuedRef.current || !missionScript) {
      return;
    }
    
    // Poll to detect when turn 3 starts (player's turn again, turn 5 global)
    const checkForTurn3 = () => {
      // Check if it's player's turn (end turn button is enabled/visible)
      const endTurnButton = document.querySelector('.end-turn-button');
      const isPlayerTurn = endTurnButton && 
                          !endTurnButton.disabled && 
                          endTurnButton.style.pointerEvents !== 'none';
      
      // Check if we're on turn 5 (global) - player turn 3
      // We can detect this by checking if David (order 501) is in the hand
      const orderCarousel = document.querySelector('.order-carousel');
      const hasDavid = orderCarousel?.textContent?.includes('David');
      
      // Alternative: check gameState turnNumber if available
      const isTurn5 = gameState?.turnNumber === 5;
      
      // If it's player's turn and we're on turn 5 (or David is present), we're on player turn 3
      if (isPlayerTurn && (isTurn5 || hasDavid) && !turn3TutorialsQueuedRef.current) {
        turn3TutorialsQueuedRef.current = true;

        const turn3Tutorials = missionScript.scriptedActions.filter(action => {
          return action.trigger === 'tutorial' &&
                 action.turn === 3 &&
                 action.player === 'player';
        });

        if (turn3Tutorials.length > 0) {
          console.log(`📚 Campaign: Detected turn 3, adding ${turn3Tutorials.length} tutorial(s) to queue`);
          console.log(`📚 Campaign: Current tutorial: ${currentTutorial?.stepId || currentTutorial?.title || 'none'}`);
          console.log(`📚 Campaign: Turn 3 tutorials to add:`, turn3Tutorials.map(a => a.explanation.stepId || a.explanation.title));

          turn3Tutorials.forEach(action => {
            console.log(`📚 Campaign: Adding tutorial "${action.explanation.stepId || action.explanation.title}" to queue`);
            addTutorial(action.explanation);
          });
        } else {
          console.log('📚 Campaign: Detected player turn 3 (no scripted tutorials for this turn)');
        }
      }
    };
    
    // Poll every 500ms to check for turn 3
    const pollInterval = setInterval(checkForTurn3, 500);
    
    return () => {
      clearInterval(pollInterval);
    };
  }, [turn2TutorialsQueuedRef.current, turn3TutorialsQueuedRef.current, missionScript, addTutorial, currentTutorial, gameState]);

  // Mission 1: After bot deploys Malignant Berserker (player turn 4 start), show "Finish them"
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_1') return;
    if (!turn3TutorialsQueuedRef.current || turn4FinishThemQueuedRef.current || !missionScript) {
      return;
    }

    const checkForFinishThem = () => {
      if (turn4FinishThemQueuedRef.current) return;

      const endTurnButton = document.querySelector('.end-turn-button');
      const isPlayerTurn = endTurnButton &&
                          !endTurnButton.disabled &&
                          endTurnButton.style.pointerEvents !== 'none';
      const isTurn7 = gameState?.turnNumber === 7;
      const opponentFlank = document.querySelector('.battle-zone.opponent-flank');
      const hasMalignantBerserker = opponentFlank?.textContent?.includes('Malignant Berserker');

      if (!isPlayerTurn || (!isTurn7 && !hasMalignantBerserker)) return;

      const finishThemAction = missionScript.scriptedActions.find(action =>
        action.trigger === 'tutorial' &&
        action.turn === 4 &&
        action.player === 'player' &&
        action.explanation?.stepId === 'finish_them'
      );

      if (!finishThemAction) return;

      turn4FinishThemQueuedRef.current = true;
      console.log('📚 Campaign: Detected player turn 4 after Malignant Berserker deploy — adding finish_them tutorial');
      addTutorial(finishThemAction.explanation);
    };

    checkForFinishThem();
    const pollInterval = setInterval(checkForFinishThem, 500);
    return () => clearInterval(pollInterval);
  }, [missionId, turn3TutorialsQueuedRef.current, turn4FinishThemQueuedRef.current, missionScript, addTutorial, gameState?.turnNumber]);

  // Mission 1: After bot attacks the general (counter strike dialog), add remaining turn 4 tutorials
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_1') return;
    if (!turn4FinishThemQueuedRef.current || counterStrikeTutorialQueuedRef.current || !missionScript) {
      return;
    }

    const checkForCounterStrikeTutorials = () => {
      if (counterStrikeTutorialQueuedRef.current) return;

      const counterStrikeDialog = document.querySelector('.counter-strike-overlay');
      const hasCounterStrike = counterStrikeDialog &&
                               counterStrikeDialog.style.display !== 'none' &&
                               window.getComputedStyle(counterStrikeDialog).display !== 'none';

      if (!hasCounterStrike) return;

      const turn4Tutorials = missionScript.scriptedActions.filter(action => {
        return action.trigger === 'tutorial' &&
               action.turn === 4 &&
               action.player === 'player' &&
               action.explanation?.stepId !== 'finish_them';
      });

      if (turn4Tutorials.length === 0) return;

      counterStrikeTutorialQueuedRef.current = true;
      console.log(`📚 Campaign: Detected counter strike dialog, adding ${turn4Tutorials.length} turn 4 tutorial(s)`);
      turn4Tutorials.forEach(action => {
        console.log(`📚 Campaign: Adding tutorial "${action.explanation.stepId || action.explanation.title}" to queue`);
        addTutorial(action.explanation);
      });
    };

    checkForCounterStrikeTutorials();
    const pollInterval = setInterval(checkForCounterStrikeTutorials, 500);
    return () => clearInterval(pollInterval);
  }, [missionId, turn4FinishThemQueuedRef.current, counterStrikeTutorialQueuedRef.current, missionScript, addTutorial]);

  // Mission 2: When counter strike dialog appears, queue "Strike the Berserker" tutorial
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_2' || mission2CounterStrikeTutorialQueuedRef.current) {
      return;
    }

    const checkForMission2CounterStrike = () => {
      if (mission2CounterStrikeTutorialQueuedRef.current) return;

      const counterStrikeDialog = document.querySelector('.counter-strike-overlay');
      const hasCounterStrike = counterStrikeDialog &&
                               counterStrikeDialog.style.display !== 'none' &&
                               window.getComputedStyle(counterStrikeDialog).display !== 'none';

      if (hasCounterStrike) {
        mission2CounterStrikeTutorialQueuedRef.current = true;
        console.log('📚 Campaign: Mission 2 - Detected counter strike dialog, adding Strike the Berserker tutorial');
        addTutorial({
          stepId: 'strike_the_berserker',
          title: 'Strike the Berserker',
          text: 'The Malignant Berserker completely obliterated one of our barriers. Although this senseless destruction is of course bad, it also has a positive aspect, since we get another order. If the order we get has a special ability called Counter Strike we can even use it instantly at no cost, thus potentially shifting the tide in our favor. Click "Strike!" and target the Malignant Berserker.'
        });
      }
    };

    checkForMission2CounterStrike();
    const pollInterval = setInterval(checkForMission2CounterStrike, 500);

    return () => clearInterval(pollInterval);
  }, [missionId, addTutorial]);

  // Mission 2: Block Shield Unit card until "Shield and attack" tutorial
  useEffect(() => {
    if (missionId !== 'chapter_1_mission_2') return;

    const allowShield = !currentTutorial ||
      currentTutorial.title === "Shield and attack" ||
      currentTutorial.title === "Chief Engineer destroyed" ||
      currentTutorial.title === "Duplicate" ||
      currentTutorial.title === "Kill threats" ||
      currentTutorial.title === "Final blow";

    let overlay = document.getElementById('shield-unit-block-overlay');
    if (allowShield) {
      if (overlay) { overlay.style.display = 'none'; }
      return;
    }

    if (!overlay) {
      overlay = document.createElement('div');
      overlay.id = 'shield-unit-block-overlay';
      overlay.style.position = 'fixed';
      overlay.style.zIndex = '99999';
      overlay.style.pointerEvents = 'auto';
      overlay.style.backgroundColor = 'transparent';
      document.body.appendChild(overlay);
    }

    const updateOverlay = () => {
      const cards = document.querySelectorAll('.carousel-order-card');
      let found = false;
      for (const card of cards) {
        if (card.textContent?.includes('Shield Unit') || card.textContent?.includes('Shield')) {
          const name = card.textContent || '';
          if (name.includes('Shield Unit') || (name.includes('Shield') && !name.includes('Blaster'))) {
            const rect = card.getBoundingClientRect();
            overlay.style.display = 'block';
            overlay.style.left = `${rect.left}px`;
            overlay.style.top = `${rect.top}px`;
            overlay.style.width = `${rect.width}px`;
            overlay.style.height = `${rect.height}px`;
            found = true;
            break;
          }
        }
      }
      if (!found && overlay) overlay.style.display = 'none';
    };

    updateOverlay();
    const iv = setInterval(updateOverlay, 200);
    const handleResize = () => updateOverlay();
    window.addEventListener('resize', handleResize);

    return () => {
      clearInterval(iv);
      window.removeEventListener('resize', handleResize);
      if (overlay) overlay.style.display = 'none';
    };
  }, [currentTutorial, missionId]);

  // Mission 2: Auto-advance "Deploy your forces" when player clicks End Turn (both units deployed)
  useEffect(() => {
    if (currentTutorial?.stepId !== 'deploy_your_forces') return;

    const onEndTurn = (e) => {
      const btn = e.target.closest('.end-turn-button');
      if (!btn) return;
      const frontZone = document.querySelector('.battle-zone.front');
      if (frontZone) {
        const text = frontZone.textContent || '';
        if (text.includes('Chief Engineer') && text.includes('Youthful Patriot')) {
          console.log('📜 Campaign M2: Both units deployed to front, advancing "Deploy your forces"');
          setTimeout(() => continueTutorial(), 300);
        }
      }
    };
    document.addEventListener('click', onEndTurn, true);
    return () => document.removeEventListener('click', onEndTurn, true);
  }, [currentTutorial, continueTutorial]);

  // Mission 2: Auto-advance "Inferno Drop" when berserkers are wiped from opponent zones
  useEffect(() => {
    if (currentTutorial?.stepId !== 'inferno_drop') return;

    const checkInfernoDropResolved = () => {
      const opFront = document.querySelector('.battle-zone.opponent-front');
      const opFlank = document.querySelector('.battle-zone.opponent-flank');
      const frontText = opFront?.textContent || '';
      const flankText = opFlank?.textContent || '';
      if (!frontText.includes('Berserker') && !flankText.includes('Berserker')) {
        console.log('📜 Campaign M2: Berserkers wiped, advancing "Inferno Drop"');
        continueTutorial();
        return true;
      }
      return false;
    };
    const iv = setInterval(checkInfernoDropResolved, 500);
    return () => clearInterval(iv);
  }, [currentTutorial, continueTutorial]);

  // Mission 2: Auto-advance "Shield and attack" when player clicks End Turn
  useEffect(() => {
    if (currentTutorial?.stepId !== 'shield_and_attack') return;

    const onEndTurn = (e) => {
      const btn = e.target.closest('.end-turn-button');
      if (!btn) return;
      console.log('📜 Campaign M2: End turn clicked, advancing "Shield and attack"');
      setTimeout(() => continueTutorial(), 300);
    };
    document.addEventListener('click', onEndTurn, true);
    return () => document.removeEventListener('click', onEndTurn, true);
  }, [currentTutorial, continueTutorial]);

  // Mission 2: Auto-advance "Duplicate" when Viper Tank is deployed in flank
  useEffect(() => {
    if (currentTutorial?.stepId !== 'duplicate') return;

    const checkTankInFlank = () => {
      const flankZone = document.querySelector('.battle-zone.flank');
      if (flankZone && (flankZone.textContent?.includes('Viper Tank') || flankZone.textContent?.includes('Steel Viper'))) {
        console.log('📜 Campaign M2: Viper Tank in flank, advancing "Duplicate"');
        continueTutorial();
        return true;
      }
      return false;
    };
    if (checkTankInFlank()) return;
    const iv = setInterval(() => {
      if (checkTankInFlank()) clearInterval(iv);
    }, 500);
    return () => clearInterval(iv);
  }, [currentTutorial, continueTutorial]);

  // Mission 2: Auto-advance "Kill threats" when player clicks End Turn (passes)
  useEffect(() => {
    if (currentTutorial?.stepId !== 'kill_threats') return;

    const onEndTurn = (e) => {
      const btn = e.target.closest('.end-turn-button');
      if (!btn) return;
      console.log('📜 Campaign M2: End turn clicked, advancing "Kill threats"');
      setTimeout(() => continueTutorial(), 300);
    };
    document.addEventListener('click', onEndTurn, true);
    return () => document.removeEventListener('click', onEndTurn, true);
  }, [currentTutorial, continueTutorial]);

  // Mission 3: Auto-advance "Confuse" tutorial when Whispers of Kek (228) is played
  useEffect(() => {
    if (currentTutorial?.stepId !== 'confuse') return;
    let hasAdvanced = false;
    const checkWhispersPlayed = () => {
      if (hasAdvanced) return;
      const actionHistory = document.querySelector('.action-history, [class*="action-history"], [class*="ActionHistory"]');
      if (actionHistory) {
        const text = actionHistory.textContent || '';
        if (text.includes('Whispers') || text.includes('Wispers')) {
          hasAdvanced = true;
          console.log('✅ Confuse tutorial: Whispers of Kek played - advancing');
          continueTutorial();
          return;
        }
      }
      const carousel = document.querySelector('.order-carousel');
      const carouselText = carousel?.textContent || '';
      if (!carouselText.includes('Whispers') && !carouselText.includes('Wispers')) {
        hasAdvanced = true;
        console.log('✅ Confuse tutorial: Whispers of Kek no longer in hand - advancing');
        continueTutorial();
      }
    };
    const iv = setInterval(checkWhispersPlayed, 300);
    return () => clearInterval(iv);
  }, [currentTutorial, continueTutorial]);

  // Mission 3: Auto-advance "Destroy the Drone" tutorial when opponent drone is destroyed
  useEffect(() => {
    if (currentTutorial?.stepId !== 'destroy_the_drone') return;
    let hasAdvanced = false;
    const checkDroneDestroyed = () => {
      if (hasAdvanced) return;
      const droneZone = document.querySelector('.opponent-drone-zone');
      if (!droneZone) return;
      const droneCards = droneZone.querySelectorAll('[data-drone-index]');
      if (droneCards.length === 0) {
        hasAdvanced = true;
        console.log('✅ Destroy the Drone tutorial: Opponent drone destroyed - advancing');
        continueTutorial();
      }
    };
    const iv = setInterval(checkDroneDestroyed, 300);
    return () => clearInterval(iv);
  }, [currentTutorial, continueTutorial]);

  // Mission 3: After player plays Missile Barrage (316), advance to next tutorial ("Your Drones").
  useEffect(() => {
    if (!suppressTutorialDialog) return;
    let hasFired = false;
    const check = () => {
      if (hasFired) return;
      const actionHistory = document.querySelector('.action-history, [class*="action-history"], [class*="ActionHistory"]');
      if (actionHistory) {
        const text = actionHistory.textContent || '';
        if (text.includes('Missile Barrage') || text.includes('Missle Barrage')) {
          hasFired = true;
          console.log('✅ Missile Barrage played - advancing to next tutorial');
          setSuppressTutorialDialog(false);
          continueTutorial();
          return;
        }
      }
      const carousel = document.querySelector('.order-carousel');
      if (carousel) {
        const carouselText = carousel.textContent || '';
        if (carouselText.length > 0 && !carouselText.includes('Missile Barrage') && !carouselText.includes('Missle Barrage')) {
          hasFired = true;
          console.log('✅ Missile Barrage no longer in hand - advancing to next tutorial');
          setSuppressTutorialDialog(false);
          continueTutorial();
        }
      }
    };
    const iv = setInterval(check, 300);
    return () => clearInterval(iv);
  }, [suppressTutorialDialog, continueTutorial]);

  // Mission 3: Auto-advance "Counter Drones" tutorial when Missile Barrage (316) is played
  useEffect(() => {
    if (currentTutorial?.stepId !== 'counter_drones') return;
    let hasAdvanced = false;
    const checkMissileBarragePlayed = () => {
      if (hasAdvanced) return;
      const actionHistory = document.querySelector('.action-history, [class*="action-history"], [class*="ActionHistory"]');
      if (actionHistory) {
        const text = actionHistory.textContent || '';
        if (text.includes('Missile Barrage')) {
          hasAdvanced = true;
          console.log('✅ Counter Drones tutorial: Missile Barrage played - advancing');
          continueTutorial();
          return;
        }
      }
      const carousel = document.querySelector('.order-carousel');
      const carouselText = carousel?.textContent || '';
      if (!carouselText.includes('Missile Barrage')) {
        hasAdvanced = true;
        console.log('✅ Counter Drones tutorial: Missile Barrage no longer in hand - advancing');
        continueTutorial();
      }
    };
    const iv = setInterval(checkMissileBarragePlayed, 300);
    return () => clearInterval(iv);
  }, [currentTutorial, continueTutorial]);

  // Start campaign mission on mount
  useEffect(() => {
    if (!missionId || gameId) return; // Already started or no mission ID

    const startMission = async () => {
      try {
        let authToken = token || localStorage.getItem('token');
        const currentUserId = userId || parseInt(localStorage.getItem('userId'));

        if (!currentUserId) {
          console.error('❌ Campaign: No userId available');
          await reportCampaignStartFailure('no_user_id', missionId, null, 'userId and localStorage userId both empty');
          navigate('/campaign');
          return;
        }

        const refreshTokenValue = localStorage.getItem('refreshToken');
        const needsRefresh = !authToken || !isTokenValid(authToken) || needsTokenRefresh(authToken);
        if (needsRefresh && refreshTokenValue) {
          try {
            console.log('🔄 Campaign: Token missing, expired or about to expire, refreshing...');
            authToken = await refreshToken(refreshTokenValue);
            localStorage.setItem('token', authToken);
            console.log('✅ Campaign: Token refreshed successfully');
          } catch (refreshErr) {
            console.error('❌ Campaign: Token refresh failed:', refreshErr);
            await reportCampaignStartFailure('token_refresh_failed', missionId, currentUserId, refreshErr?.message || String(refreshErr));
            navigate('/campaign');
            return;
          }
        }
        if (!authToken) {
          console.error('❌ Campaign: No auth token available');
          await reportCampaignStartFailure('no_token', missionId, currentUserId, 'No token and no refresh token or refresh failed');
          navigate('/campaign');
          return;
        }

        const doFetch = async (tokenToUse) => {
          const selectedLanguage = (localStorage.getItem('i18nextLng') || 'en-us').toLowerCase();
          return fetch(`${API_URL}/campaign/start-mission?lang=${encodeURIComponent(selectedLanguage)}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${tokenToUse}`
            },
            body: JSON.stringify({
              missionId: missionId,
              userId: currentUserId,
              language: selectedLanguage
            })
          });
        };

        console.log(`🎮 Campaign: Starting mission ${missionId} for user ${currentUserId}`);
        let response = await doFetch(authToken);

        if (response.status === 403) {
          const errorBody = await response.text();
          const isInvalidToken = errorBody.includes('Invalid token');
          if (isInvalidToken && refreshTokenValue) {
            try {
              console.log('🔄 Campaign: 403 Invalid token - refreshing and retrying...');
              const newToken = await refreshToken(refreshTokenValue);
              localStorage.setItem('token', newToken);
              response = await doFetch(newToken);
              if (response.ok) {
                console.log('✅ Campaign: Retry with refreshed token succeeded');
              }
            } catch (retryErr) {
              console.error('❌ Campaign: Retry after 403 failed:', retryErr);
              await reportCampaignStartFailure('api_error', missionId, currentUserId, `status=403 body=${errorBody}`);
              throw new Error(`Failed to start campaign mission: 403 ${errorBody}`);
            }
          }
        }

        if (!response.ok) {
          const errorText = await response.text();
          console.error(`❌ Campaign: Failed to start mission. Status: ${response.status}, Response: ${errorText}`);
          let errorMessage = errorText;
          try {
            const parsed = JSON.parse(errorText);
            if (parsed && typeof parsed.error === 'string') errorMessage = parsed.error;
          } catch (_) {}
          showNotification(errorMessage, 'error', 6000);
          await reportCampaignStartFailure('api_error', missionId, currentUserId, `status=${response.status} body=${errorText}`);
          setTimeout(() => navigate('/campaign'), 1500);
          return;
        }

        const data = await response.json();
        console.log(`✅ Campaign: Mission started successfully. gameId: ${data.gameId}`);
        console.log(`📊 Campaign: Game state received:`, {
          hasGameState: !!data.gameState,
          campaignMode: data.gameState?.campaignMode,
          hasScriptState: !!data.gameState?.scriptState,
          tutorialQueue: data.gameState?.scriptState?.tutorialQueue,
          tutorialQueueLength: data.gameState?.scriptState?.tutorialQueue?.length || 0,
          fullScriptState: data.gameState?.scriptState
        });
        setGameId(data.gameId);
        setGameState(data.gameState);
        setMissionScript(data.gameState?.missionScript || null);
        if (data.gameState?.campaignMode && data.gameState?.scriptState) lastCampaignStateRef.current = { campaignMode: true, missionId: data.gameState.missionId, scriptState: data.gameState.scriptState };
        
        // Don't navigate - keep CampaignBattleUI mounted so TutorialDialog can render
        // BattleUI will be rendered inside CampaignBattleUI
      } catch (error) {
        console.error('❌ Campaign: Error starting campaign mission:', error);
        const currentUserId = userId || parseInt(localStorage.getItem('userId'));
        await reportCampaignStartFailure('exception', missionId, currentUserId, error?.message || String(error));
        navigate('/campaign');
      }
    };

    startMission();
  }, [missionId, userId, gameId, navigate, token, showNotification]);

  // Handle tutorial continue
  const handleTutorialContinue = useCallback(() => {
    // Check if this is the final tutorial - disable campaign script after it
    if (currentTutorial?.stepId === 'defeat_your_opponent') {
      disableCampaignScript().catch(err => {
        console.error(`❌ Campaign: Failed to disable script:`, err);
      });
    }

    // Mission 4 "End the threat": disable campaign script for free play
    if (currentTutorial?.stepId === 'end_the_threat') {
      disableCampaignScript().catch(err => {
        console.error(`❌ Campaign: Failed to disable script:`, err);
      });
    }

    // Mission 3 "Missile Barrage": suppress dialog so player can see the flank and play the strategem.
    // Do NOT advance the tutorial yet — currentTutorial stays "Missile Barrage" so backend keeps allowing strategem 316.
    // Advance happens in the useEffect that detects Missile Barrage was played.
    if (currentTutorial?.stepId === 'missile_barrage') {
      setSuppressTutorialDialog(true);
      return;
    }

    // Mission 2 "Brace for impact": auto-pass turn when Continue is clicked (bot will attack)
    const wasBraceForImpact = currentTutorial?.stepId === 'brace_for_impact';
    
    continueTutorial();

    if (wasBraceForImpact) {
      setTimeout(() => {
        const endTurnButton = document.querySelector('.end-turn-button');
        if (endTurnButton && !endTurnButton.disabled) {
          console.log('📜 Campaign: Auto-passing turn after "Brace for impact" tutorial');
          endTurnButton.click();
        }
      }, 400);
    }
  }, [continueTutorial, currentTutorial, disableCampaignScript]);

  // If no game state yet, show loading
  if (!gameState || !gameId) {
    return (
      <>
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          color: 'white',
          fontSize: '1.2em',
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
        }}>
          Loading campaign mission...
        </div>
        {NotificationComponent}
      </>
    );
  }

  return (
    <>
      {NotificationComponent}
      {battleUIElementRef.current}
      {currentTutorial && !suppressTutorialDialog && (
        <SimpleTutorialDialog
            explanation={currentTutorial}
            missionId={missionId}
            onContinue={handleTutorialContinue}
            voiceKey={currentTutorial?.stepId || currentTutorial?.title}
          hideContinueButton={overlayStepConfig ? overlayStepConfig.endDismissal !== 'continue' : (['opponents_turn', 'deploy', 'end_turn_1', 'end_turn_2', 'special_units', 'out_of_reach', 'attack_the_general', 'counter_strike', 'finish_them', 'defeat_your_opponent', 'deploy_your_forces', 'inferno_drop', 'shield_and_attack', 'duplicate', 'kill_threats', 'final_blow', 'confuse', 'destroy_the_drone', 'deploy_effects', 'your_drones', 'counter_drones', 'use_your_drone', 'samuel_johnson', 'mulligan', 'advanced_tactics', 'hold_the_line', 'mentora', 'shield_mastery', 'patience', 'clearing_the_field', 'well_done', 'reinforcements', 'harrals_betrayal', 'maats_strength', 'end_the_threat'].includes(currentTutorial.stepId || ''))}
          topOffset={missionId === 'chapter_1_mission_3' ? 50 : 0}
          />
      )}
    </>
  );
}

export default CampaignBattleUI;

